/**
 * Agent Dashboard - Modern Claude-Inspired Design
 * Clean, professional interface matching main CRM aesthetic
 */

import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Button, Table, Badge, Modal, Form, Alert, Spinner, Dropdown } from 'react-bootstrap';
import axiosInstance from '../../helpers/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHome,
  faUsers, 
  faSearch, 
  faChartLine, 
  faEnvelope,
  faLink,
  faCopy,
  faSignOutAlt,
  faCog,
  faCheckCircle,
  faUserPlus,
  faExternalLinkAlt,
  faBell,
  faQuestionCircle,
  faEllipsisV,
  faFilter,
  faArchive,
  faPaperPlane,
  faRobot,
  faGlobe,
  faShieldAlt,
  faCreditCard,
  faBolt,
  faRocket,
  faFileInvoiceDollar,
  faCalendarAlt,
  faExclamationCircle,
  faCrown,
  faFolderOpen,
  faDownload,
  faBuilding,
  faLandmark,
  faHandshake,
  faBullhorn,
  faComments,
  faFileAlt,
  faPhone,
} from '@fortawesome/free-solid-svg-icons';
import { faGoogle, faMicrosoft, faYahoo } from '@fortawesome/free-brands-svg-icons';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import CommunicationsTab from './CommunicationsTab';
import agentPortalService from '../../services/agentPortal.service';
import './AgentDashboardModern.css';
import InterofficeChatTab from './InterofficeChatTab';
import OnboardingReportsTab from './OnboardingReportsTab';

interface ClientData {
  id: string;
  name: string;
  email: string;
  joinedDate: string;
  lastActive: string;
  searchCount: number;
  status: 'active' | 'invited' | 'inactive';
}

interface CRMLead {
  id: string;
  lead_first_name: string;
  lead_last_name?: string;
  lead_source?: string;
  lead_email?: string;
  lead_phone?: string;
  lead_city?: string;
  lead_state?: string;
  lead_type_status_id?: number;
  created_on: string;
  updated_on?: string;
  // Additional fields that may come from the API
  status?: string;
  lead_price_range?: string;
  lead_timeframe?: string;
}

const AgentDashboardModern: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  // Check if user is KairosIQ internal employee (not broker admin)
  const userRole = (() => {
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      if (!token) return '';
      const payload = JSON.parse(atob(token.split('.')[1]));
      return (payload.role || payload.user_role || '').toLowerCase();
    } catch { return ''; }
  })();
  const isInternalEmployee = userRole === 'internalemployee' || userRole === 'internal_employee' || userRole === 'manager';
  const isSponsor = userRole === 'lender' || userRole === 'sponsor';
  const hasBrokerage = (() => { try { const u = JSON.parse(localStorage.getItem('user_info') || '{}'); return !!u.brokerage_id; } catch { return false; } })();

  // Sponsors go straight to their dashboard
  React.useEffect(() => {
    if (isSponsor) {
      window.location.href = '/sponsor/dashboard';
    }
  }, [isSponsor]);

  // RESPA acknowledgment modal
  const [respaRequired, setRespaRequired] = useState(false);
  const [respaData, setRespaData] = useState<any>(null);
  const [respaName, setRespaName] = useState('');
  const [respaSubmitting, setRespaSubmitting] = useState(false);

  // AI Disclosure acknowledgment modal
  const [aiDisclosureRequired, setAiDisclosureRequired] = useState(false);
  const [aiDisclosureData, setAiDisclosureData] = useState<any>(null);
  const [aiDisclosureName, setAiDisclosureName] = useState('');
  const [aiDisclosureSubmitting, setAiDisclosureSubmitting] = useState(false);

  // MLS Activation modal ($150/year)
  const [mlsRequired, setMlsRequired] = useState(false);
  const [mlsData, setMlsData] = useState<any>(null);
  const [mlsLoading, setMlsLoading] = useState(false);
  const [mlsDismissed, setMlsDismissed] = useState(false);

  React.useEffect(() => {
    axiosInstance.get('/sponsor/respa-status').then(({ data }) => {
      if (data.required) { setRespaRequired(true); setRespaData(data); }
    }).catch(() => {});
    axiosInstance.get('/sponsor/ai-disclosure-status').then(({ data }) => {
      if (data.required) { setAiDisclosureRequired(true); setAiDisclosureData(data); }
    }).catch(() => {});
    // Check MLS activation status
    axiosInstance.get('/mls-activation/activation-status').then(({ data }) => {
      if (data.required) { setMlsRequired(true); setMlsData(data); }
    }).catch(() => {});
    // Check URL params for MLS activation result
    const params = new URLSearchParams(window.location.search);
    if (params.get('mls_activated') === 'true') {
      toast.success('MLS activated! Property valuations are now available.');
      params.delete('mls_activated');
      window.history.replaceState({}, '', window.location.pathname + (params.toString() ? `?${params}` : ''));
    }
  }, []);

  // Real data states
  const [agentData, setAgentData] = useState<any>(null);
  const [portalSettings, setPortalSettings] = useState<any>(null);
  const [slugError, setSlugError] = useState<string>('');
  const [isCheckingSlug, setIsCheckingSlug] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [showSupportChat, setShowSupportChat] = useState(false);
  const [supportMessages, setSupportMessages] = useState<any[]>([]);
  const [supportConvId, setSupportConvId] = useState('');
  const [supportMsg, setSupportMsg] = useState('');
  const [sendingSupport, setSendingSupport] = useState(false);
  const [crmApiKey, setCrmApiKey] = useState('');
  const [crmType, setCrmType] = useState<string>('crm_followup_boss');
  const [crmValidating, setCrmValidating] = useState(false);
  const [crmImporting, setCrmImporting] = useState(false);
  const [crmStatus, setCrmStatus] = useState<any>(null);
  const [savedCrmCredentials, setSavedCrmCredentials] = useState<any[]>([]);

  const loadCrmCredentials = async () => {
    try {
      const { data } = await axiosInstance.get('/api-credentials');
      if (data.success) {
        const creds = data.data?.credentials || data.data || data.credentials || [];
        setSavedCrmCredentials(Array.isArray(creds) ? creds : []);
      }
    } catch { }
  };
  const [clients, setClients] = useState<ClientData[]>([]);
  const [crmLeads, setCrmLeads] = useState<CRMLead[]>([]);
  const [searchProfiles, setSearchProfiles] = useState<any>(null);
  const [stats, setStats] = useState({
    totalClients: 0,
    activeThisWeek: 0,
    totalSearches: 0,
    newLeads: 0,
    pendingInvitations: 0,
    averageSearchesPerClient: 0
  });
  
  // UI states
  // Set initial view from URL path
  const getViewFromPath = (): 'overview' | 'clients' | 'profiles' | 'analytics' | 'ai-coach' | 'settings' | 'marketing' | 'chat' | 'reports' | 'website' | 'communications' => {
    const path = window.location.pathname;
    if (path.includes('/customers') || path.includes('/clients')) return 'clients';
    if (path.includes('/communications')) return 'communications';
    if (path.includes('/profiles')) return 'profiles';
    if (path.includes('/analytics')) return 'analytics';
    if (path.includes('/ai-coach')) return 'ai-coach';
    if (path.includes('/marketing')) return 'marketing';
    if (path.includes('/reports')) return 'reports';
    if (path.includes('/chat')) return 'chat';
    if (path.includes('/website')) return 'website';
    if (path.includes('/settings')) return 'settings';
    return 'overview';
  };
  const [activeView, setActiveView] = useState<'overview' | 'clients' | 'profiles' | 'analytics' | 'ai-coach' | 'settings' | 'help' | 'classifications' | 'marketing' | 'chat' | 'reports' | 'website' | 'communications'>(getViewFromPath());

  // Classification table state
  const [classLeads, setClassLeads] = useState<any[]>([]);
  const [classLoading, setClassLoading] = useState(false);
  const [classFilter, setClassFilter] = useState('all');
  const [classSearch, setClassSearch] = useState('');
  const [editingNote, setEditingNote] = useState<string | null>(null);
  const [noteText2, setNoteText2] = useState('');
  const [editingField, setEditingField] = useState<{ id: string; field: string } | null>(null);
  const [editFieldValue, setEditFieldValue] = useState('');
  const [classPage, setClassPage] = useState(1);
  const [classTotalCount, setClassTotalCount] = useState(-1);
  const [classHasMore, setClassHasMore] = useState(false);
  const [classStageCounts, setClassStageCounts] = useState<Record<string, number>>({});

  const updateLeadField = async (leadId: string, field: string, value: string) => {
    try {
      const body: any = {};
      if (field === 'firstName') body.firstName = value;
      if (field === 'lastName') body.lastName = value;
      if (field === 'email') body.email = value;
      if (field === 'phone') body.phone = value;
      await axiosInstance.post(`/ai-coach/classifications/${leadId}/update-lead`, body);
      setClassLeads(prev => prev.map(l => {
        if (l.id !== leadId) return l;
        if (field === 'firstName') return { ...l, lead_first_name: value };
        if (field === 'lastName') return { ...l, lead_last_name: value };
        if (field === 'email') return { ...l, lead_email: value };
        if (field === 'phone') return { ...l, lead_phone: value };
        return l;
      }));
    } catch { }
    setEditingField(null);
  };

  const classStages = [
    { value: 'active_buyer', label: 'Active Buyer', color: '#27ae60' },
    { value: 'active_seller', label: 'Active Seller', color: '#2980b9' },
    { value: 'buyer_prospect', label: 'Buyer Prospect', color: '#16a085' },
    { value: 'seller_prospect', label: 'Seller Prospect', color: '#8e44ad' },
    { value: 'past_buyer', label: 'Past Buyer', color: '#e67e22' },
    { value: 'past_seller', label: 'Past Seller', color: '#d35400' },
    { value: 'sphere_of_influence', label: 'Sphere of Influence', color: '#f39c12' },
    { value: 'referral_source', label: 'Referral Source', color: '#C9952A' },
    { value: 'investor', label: 'Investor', color: '#1abc9c' },
    { value: 'nurture', label: 'Nurture', color: '#3498db' },
    { value: 'cold_reactivation', label: 'Cold Reactivation', color: '#95a5a6' },
    { value: 'new_contact', label: 'New Contact', color: '#bdc3c7' },
    { value: 'vendor', label: 'Vendor', color: '#7f8c8d' },
    { value: 'unknown', label: 'Unknown', color: '#ccc' },
  ];

  const loadClassifications = async (page = 1, stage = classFilter, search = classSearch) => {
    setClassLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '200' });
      if (stage && stage !== 'all') params.set('stage', stage);
      if (search) params.set('search', search);
      const { data } = await axiosInstance.get(`/ai-coach/classifications?${params}`);
      if (data.success) {
        setClassLeads(page === 1 ? (data.leads || []) : [...classLeads, ...(data.leads || [])]);
        setClassTotalCount(data.totalCount || 0);
        setClassHasMore(data.hasMore || false);
        setClassPage(page);
        if (data.stageCounts) {
          const counts: Record<string, number> = {};
          data.stageCounts.forEach((s: any) => { counts[s.stage || 'unknown'] = s.cnt; });
          setClassStageCounts(counts);
        }
      }
    } catch { }
    setClassLoading(false);
  };

  const updateClassification = async (leadId: string, stage?: string, notes?: string) => {
    try {
      await axiosInstance.post(`/ai-coach/classifications/${leadId}`, { stage, notes });
      setClassLeads(prev => prev.map(l => l.id === leadId ? { ...l, consumer_journey_stage: stage || l.consumer_journey_stage, relationship_notes: notes !== undefined ? notes : l.relationship_notes } : l));
    } catch { }
  };

  // Plan gating
  const [userPlan, setUserPlan] = useState<string>('trial');
  const isSponsorPlan = ['sponsor-pro', 'sponsor-enterprise', 'sponsor-demo', 'sponsor-admin'].includes(userPlan);
  const hasAIPro = ['ai-pro', 'internal'].includes(userPlan) || isSponsorPlan;

  // AI Coach states
  const [coachQueue, setCoachQueue] = useState<any[]>([]);
  const [coachLoading, setCoachLoading] = useState(false);
  const [coldLeads, setColdLeads] = useState<any[]>([]);
  const [generatingDrafts, setGeneratingDrafts] = useState(false);
  const [sendingDraft, setSendingDraft] = useState<string | null>(null);
  const [editingDraft, setEditingDraft] = useState<string | null>(null);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [upcomingTasks, setUpcomingTasks] = useState<any[]>([]);
  const [urgentAlerts, setUrgentAlerts] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'invited' | 'inactive'>('all');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteSent, setInviteSent] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [dashboardStats, setDashboardStats] = useState<any>(null);
  
  // Loading states for each view
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [hasLoadedClients, setHasLoadedClients] = useState(false);
  const [hasLoadedProfiles, setHasLoadedProfiles] = useState(false);

  // Email integration states
  const [connectedEmails, setConnectedEmails] = useState<any[]>([]);
  const [loadingEmailConnect, setLoadingEmailConnect] = useState(false);
  // KOPS-611: Yahoo / IMAP connect (app password)
  const [showYahoo, setShowYahoo] = useState(false);
  const [yahooEmail, setYahooEmail] = useState('');
  const [yahooPassword, setYahooPassword] = useState('');
  const [yahooConsent, setYahooConsent] = useState(false);
  const [yahooLoading, setYahooLoading] = useState(false);
  const [yahooError, setYahooError] = useState<string | null>(null);
  const [yahooSuccess, setYahooSuccess] = useState<string | null>(null);
  const [disconnectingEmail, setDisconnectingEmail] = useState<number | null>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Lead Discovery
  const [showDiscovery, setShowDiscovery] = useState(false);
  const [discoveredContacts, setDiscoveredContacts] = useState<any[]>([]);
  const [selectedDiscovered, setSelectedDiscovered] = useState<Set<string>>(new Set());
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  const [discoveryProgress, setDiscoveryProgress] = useState(0);
  const [importingLeads, setImportingLeads] = useState(false);

  // Poll for discovery results
  const startDiscoveryPolling = () => {
    setShowDiscovery(true);
    setDiscoveryLoading(true);
    setDiscoveryProgress(0);
    setDiscoveredContacts([]);
    // First call triggers the background scan
    axiosInstance.get('/agents/discover-leads').then(() => {
      // Start polling every 3 seconds
      const pollInterval = setInterval(async () => {
        try {
          const res = await axiosInstance.get('/agents/discover-leads');
          const data = res.data;
          if (data.status === 'complete') {
            clearInterval(pollInterval);
            setDiscoveredContacts(data.contacts || []);
            setDiscoveryLoading(false);
            setDiscoveryProgress(100);
          } else if (data.status === 'error') {
            clearInterval(pollInterval);
            setDiscoveryLoading(false);
            toast.error(data.message || 'Discovery failed');
          } else if (data.status === 'scanning') {
            setDiscoveryProgress(data.progress || 0);
          }
        } catch {
          clearInterval(pollInterval);
          setDiscoveryLoading(false);
        }
      }, 3000);
    }).catch(() => setDiscoveryLoading(false));
  };

  // Fetch real data on component mount
  useEffect(() => {
    fetchDashboardData();
    fetchConnectedEmails();
    fetchCoachQueue(); // Load AI Coach queue count for overview card
    // Fetch plan for feature gating
    axiosInstance.get('/agents/plan').then(res => setUserPlan(res.data?.plan || 'trial')).catch(() => {});

    // Handle OAuth redirect success
    const params = new URLSearchParams(window.location.search);
    if (params.get('email_connected') === 'true') {
      toast.success('Email account connected! Scanning for your clients...');
      params.delete('email_connected');
      params.delete('tab');
      const newUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
      window.history.replaceState({}, '', newUrl);
      // Trigger lead discovery after Gmail connect
      startDiscoveryPolling();
    }
  }, []);

  const fetchConnectedEmails = async () => {
    try {
      const response = await axiosInstance.get('/connected-emails');
      setConnectedEmails(response.data?.data || []);
    } catch (err) {
      console.log('Could not fetch connected emails:', err);
    }
  };

  const fetchCoachQueue = async () => {
    try {
      const response = await axiosInstance.get('/ai-coach/queue');
      setCoachQueue(response.data?.queue || []);
    } catch (err) {
      console.log('Could not fetch AI coach queue:', err);
    }
  };

  const fetchUrgentAlerts = async () => {
    try {
      const response = await axiosInstance.get('/ai-coach/urgent-alerts');
      setUrgentAlerts(response.data?.alerts || []);
    } catch { }
  };

  const fetchUpcomingTasks = async () => {
    try {
      const response = await axiosInstance.get('/ai-coach/upcoming');
      setUpcomingTasks(response.data?.tasks || []);
    } catch (err) {
      console.log('Could not fetch upcoming tasks:', err);
    }
  };

  const fetchColdLeads = async () => {
    try {
      const response = await axiosInstance.get('/ai-coach/cold-leads');
      setColdLeads(response.data?.leads || []);
    } catch (err) {
      console.log('Could not fetch cold leads:', err);
    }
  };

  const handleGenerateDrafts = async () => {
    setGeneratingDrafts(true);
    try {
      const response = await axiosInstance.post('/ai-coach/batch-draft', {});
      toast.success(`Jasper drafted ${response.data?.count || 0} follow-ups for you`);
      await fetchCoachQueue();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to generate drafts');
    }
    setGeneratingDrafts(false);
  };

  const handleSendDraft = async (queueId: string, fromAccountId?: string) => {
    setSendingDraft(queueId);
    try {
      await axiosInstance.post(`/ai-coach/queue/${queueId}/send`, {
        fromAccountId,
        editedSubject: editingDraft === queueId ? editSubject : undefined,
        editedBody: editingDraft === queueId ? editBody : undefined
      });
      toast.success('Email sent!');
      setCoachQueue(prev => prev.filter(q => q.id !== queueId));
      setEditingDraft(null);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to send');
    }
    setSendingDraft(null);
  };

  const handleDismissDraft = async (queueId: string) => {
    try {
      await axiosInstance.post(`/ai-coach/queue/${queueId}/dismiss`);
      setCoachQueue(prev => prev.filter(q => q.id !== queueId));
    } catch (err) {
      console.error('Failed to dismiss:', err);
    }
  };

  const handleConnectEmail = (provider: 'google' | 'microsoft') => {
    setLoadingEmailConnect(true);
    // Pass JWT as query param since browser navigation doesn't send Authorization header
    // The server's extractToken helper supports ?token= for OAuth flows
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
    if (!token) {
      toast.error('Please log in again to connect your email.');
      setLoadingEmailConnect(false);
      return;
    }
    const baseUrl = window.location.origin;
    window.location.href = `${baseUrl}/api/${provider}/connect/auth?token=${token}`;
  };

  // KOPS-611: Yahoo / IMAP connect via app password (no OAuth).
  const openYahoo = () => {
    setYahooEmail('');
    setYahooPassword('');
    setYahooConsent(false);
    setYahooError(null);
    setYahooSuccess(null);
    setShowYahoo(true);
  };
  const submitYahoo = async () => {
    setYahooError(null);
    if (!yahooEmail.trim() || !yahooPassword.trim()) {
      setYahooError('Enter your Yahoo email and the 16-character app password.');
      return;
    }
    setYahooLoading(true);
    try {
      const { data } = await axiosInstance.post('/connected-emails/connect-yahoo', {
        email: yahooEmail.trim(),
        app_password: yahooPassword,
        provider: 'yahoo',
      });
      if (data?.success) {
        setYahooSuccess(`Connected! Found ${data.contactsCreated ?? 0} contacts — downloading your emails and classifying your book now.`);
        setYahooPassword('');
        fetchConnectedEmails();
      } else {
        setYahooError(data?.error || 'Could not connect. Double-check your email and app password.');
      }
    } catch (err: any) {
      setYahooError(err?.response?.data?.error || 'Could not connect. Make sure 2-step verification is on and you used a freshly generated app password.');
    } finally {
      setYahooLoading(false);
    }
  };

  const handleDisconnectEmail = async (connectedEmailId: number) => {
    if (!window.confirm('Are you sure you want to disconnect this email account?')) return;

    setDisconnectingEmail(connectedEmailId);
    try {
      await axiosInstance.delete(`/connected-emails/${connectedEmailId}`);
      toast.success('Email account disconnected');
      setConnectedEmails(prev => prev.filter(e => e.id !== connectedEmailId));
    } catch (err) {
      console.error('Error disconnecting email:', err);
      toast.error('Failed to disconnect email');
    }
    setDisconnectingEmail(null);
  };

  // Load data when switching to clients view
  useEffect(() => {
    if (activeView === 'clients' && !hasLoadedClients && !loadingClients) {
      loadClientsData();
    }
  }, [activeView]);

  // Load data when switching to profiles view
  useEffect(() => {
    if (activeView === 'profiles' && !hasLoadedProfiles && !loadingProfiles) {
      loadProfilesData();
    }
  }, [activeView]);

  // Load AI Coach data when switching to ai-coach view
  useEffect(() => {
    if (activeView === 'ai-coach') {
      fetchCoachQueue();
      fetchColdLeads();
      fetchUpcomingTasks();
    }
  }, [activeView]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch agent profile
      const profileResponse = await agentPortalService.getProfile();
      setAgentData(profileResponse);
      
      // Fetch portal settings
      try {
        const settingsResponse = await agentPortalService.getPortalSettings();
        setPortalSettings(settingsResponse.data);
      } catch (error) {
        console.error('Error loading portal settings:', error);
      }

      // Fetch real dashboard stats from optimized endpoint
      try {
        const statsResponse = await axiosInstance.get('/agents/dashboard/stats');
        if (statsResponse.data.success) {
          setDashboardStats({
            ...statsResponse.data.stats,
            recentReports: statsResponse.data.recentReports || [],
            recentLeads: statsResponse.data.recentLeads || []
          });
        }
      } catch (err) {
        console.log('Could not fetch dashboard stats:', err);
      }

      // Fetch clients
      const clientsResponse = await agentPortalService.getClients();
      const clientsList = clientsResponse.clients || [];
      setClients(clientsList);

      // Also fetch CRM leads for stats
      try {
        const leadsResponse = await agentPortalService.getCRMLeads();
        const leads = leadsResponse.data || [];
        setCrmLeads(leads.slice(0, 100));

        // Calculate stats from real data
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        const monthAgo = new Date();
        monthAgo.setMonth(monthAgo.getMonth() - 1);

        setStats({
          totalClients: leads.length || clientsList.length,
          activeThisWeek: leads.filter((l: any) => new Date(l.updated_on || l.created_on) > weekAgo).length,
          totalSearches: leads.filter((l: any) => l.lead_type === 'buyer' || l.lead_source?.includes('search')).length,
          newLeads: leads.filter((l: any) => new Date(l.created_on) > monthAgo).length,
          pendingInvitations: 0,
          averageSearchesPerClient: 0
        });
      } catch (err) {
        console.log('Could not fetch leads, using clients:', err);
        setStats({
          totalClients: clientsList.length,
          activeThisWeek: 0,
          totalSearches: 0,
          newLeads: 0,
          pendingInvitations: 0,
          averageSearchesPerClient: 0
        });
      }

    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteClient = async () => {
    try {
      const nameParts = inviteName.trim().split(/\s+/);
      await agentPortalService.inviteClient({ email: inviteEmail, firstName: nameParts[0] || '', lastName: nameParts.slice(1).join(' ') || '' });
      setInviteSent(true);
      
      // Refresh clients list
      const clientsResponse = await agentPortalService.getClients();
      setClients(clientsResponse.clients);
      
      // Reset after 3 seconds
      setTimeout(() => {
        setShowInviteModal(false);
        setInviteSent(false);
        setInviteEmail('');
        setInviteName('');
      }, 3000);
    } catch (err) {
      console.error('Error inviting client:', err);
      alert('Failed to send invitation. Please try again.');
    }
  };

  const copyPortalLink = () => {
    navigator.clipboard.writeText(agentData?.portalUrl || '');
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_info');
    document.cookie = 'isLoggedInKIQ=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    navigate('/agent-portal/login');
  };

  const loadClientsData = async () => {
    try {
      setLoadingClients(true);
      console.log('Loading CRM leads data...');
      
      // Fetch CRM leads with pagination (first 100 only)
      const leadsResponse = await agentPortalService.getCRMLeads();
      const leads = leadsResponse.data || [];
      
      // Limit to first 100 leads to prevent UI freezing
      const limitedLeads = leads.slice(0, 100);
      setCrmLeads(limitedLeads);
      
      // Update stats
      setStats(prev => ({
        ...prev,
        totalClients: leads.length,
        newLeads: leads.filter((lead: CRMLead) => {
          const createdDate = new Date(lead.created_on);
          const weekAgo = new Date();
          weekAgo.setDate(weekAgo.getDate() - 7);
          return createdDate > weekAgo;
        }).length
      }));
      
      setHasLoadedClients(true);
    } catch (err) {
      console.error('Error loading clients data:', err);
      setCrmLeads([]);
    } finally {
      setLoadingClients(false);
    }
  };

  const loadProfilesData = async () => {
    try {
      setLoadingProfiles(true);
      console.log('Loading search profiles data...');
      
      // Fetch search profiles
      const profilesResponse = await agentPortalService.getSearchProfiles();
      setSearchProfiles(profilesResponse.data);
      
      setHasLoadedProfiles(true);
    } catch (err) {
      console.error('Error loading profiles data:', err);
      setSearchProfiles({ profiles: [], stats: {} });
    } finally {
      setLoadingProfiles(false);
    }
  };

  const filteredLeads = crmLeads.filter(lead => {
    const fullName = `${lead.lead_first_name || ''} ${lead.lead_last_name || ''}`.trim();
    const matchesSearch = 
      (fullName.toLowerCase().includes(searchQuery.toLowerCase()) || false) ||
      (lead.lead_email?.toLowerCase().includes(searchQuery.toLowerCase()) || false) ||
      (lead.lead_phone?.toLowerCase().includes(searchQuery.toLowerCase()) || false);
    
    const matchesFilter = filterStatus === 'all' || 
      (filterStatus === 'active' && (lead.status ? ['Working', 'Qualified'].includes(lead.status) : false)) ||
      (filterStatus === 'invited' && (!lead.status || lead.status === 'New')) ||
      (filterStatus === 'inactive' && (lead.status ? ['Lost', 'Unqualified'].includes(lead.status) : false));
    
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="modern-loading">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="modern-dashboard">
      {/* RESPA Acknowledgment Modal */}
      {respaRequired && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 16, maxWidth: 560, width: '100%', maxHeight: '90vh', overflowY: 'auto', padding: 32 }}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#fff3cd', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                <FontAwesomeIcon icon={faShieldAlt} style={{ fontSize: 24, color: '#e67e22' }} />
              </div>
              <h3 style={{ fontSize: 20, fontWeight: 700, color: '#0F1E35', margin: '0 0 4px' }}>RESPA Compliance Acknowledgment</h3>
              <p style={{ fontSize: 13, color: '#888', margin: 0 }}>Required before accessing the platform — sponsored by {respaData?.sponsorCompany}</p>
            </div>

            {[
              { title: '1. Software Access Only', text: 'KairosIQ provides software access only — not marketing services, lead generation, settlement services, or any other thing of value on behalf of the sponsoring entity.' },
              { title: '2. No Payments or Pass-Throughs', text: 'You will not receive any payments, fees, rebates, splits, or commissions from the sponsoring entity through this platform.' },
              { title: '3. No Referral Obligations', text: 'You are under NO obligation to refer mortgage business or any settlement service business back to the sponsoring entity.' },
              { title: '4. Independent Business Decisions', text: 'Nothing in the platform implies, suggests, or encourages reciprocal referrals to the sponsoring entity.' },
            ].map((p, i) => (
              <div key={i} style={{ background: '#fffaf0', borderRadius: 8, padding: 14, marginBottom: 8, borderLeft: '3px solid #e67e22' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0F1E35', marginBottom: 4 }}>{p.title}</div>
                <div style={{ fontSize: 12, color: '#555', lineHeight: 1.6 }}>{p.text}</div>
              </div>
            ))}

            <div style={{ marginTop: 20 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#0F1E35', display: 'block', marginBottom: 6 }}>
                Type your full name to acknowledge
              </label>
              <input
                value={respaName}
                onChange={e => setRespaName(e.target.value)}
                placeholder="Your full legal name"
                style={{ width: '100%', padding: '10px 14px', fontSize: 14, borderRadius: 8, border: '1px solid #ddd', outline: 'none', boxSizing: 'border-box', marginBottom: 12 }}
              />
              <button
                disabled={respaSubmitting || respaName.trim().length < 2}
                onClick={async () => {
                  setRespaSubmitting(true);
                  try {
                    const { data } = await axiosInstance.post('/sponsor/respa-ack', { memberId: respaData.memberId, typedName: respaName.trim() });
                    if (data.success) setRespaRequired(false);
                  } catch { }
                  setRespaSubmitting(false);
                }}
                style={{ width: '100%', padding: '12px', fontSize: 15, fontWeight: 700, borderRadius: 8, border: 'none', cursor: respaName.trim().length < 2 ? 'not-allowed' : 'pointer',
                  background: respaName.trim().length < 2 ? '#ccc' : 'linear-gradient(135deg, #C9952A, #E8B44A)', color: '#0F1E35',
                  opacity: respaSubmitting ? 0.6 : 1 }}
              >
                {respaSubmitting ? 'Saving...' : 'I Acknowledge These Terms'}
              </button>
              <p style={{ fontSize: 10, color: '#999', textAlign: 'center', marginTop: 8, marginBottom: 0 }}>
                This acknowledgment is recorded for RESPA compliance. You can view these terms anytime in Settings.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* AI Disclosure Acknowledgment Modal — shows after RESPA (if both required) */}
      {aiDisclosureRequired && !respaRequired && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 16, maxWidth: 560, width: '100%', maxHeight: '90vh', overflowY: 'auto', padding: 32 }}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#e3f2fd', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                <FontAwesomeIcon icon={faRobot} style={{ fontSize: 24, color: '#1565c0' }} />
              </div>
              <h3 style={{ fontSize: 20, fontWeight: 700, color: '#0F1E35', margin: '0 0 4px' }}>AI Tools Acknowledgment</h3>
              <p style={{ fontSize: 13, color: '#888', margin: 0 }}>Required before using AI-powered features</p>
            </div>

            <div style={{ background: '#f8f9fa', borderRadius: 8, padding: 16, marginBottom: 16, fontSize: 13, color: '#333', lineHeight: 1.8 }}>
              <p style={{ margin: '0 0 12px', fontWeight: 600 }}>By using AI-powered features on this platform, you acknowledge and agree:</p>
            </div>

            {[
              { title: '1. AI Is a Recommendation Tool Only', text: 'All AI-generated content — including CMA valuations, coaching recommendations, lead scores, offer analysis, and follow-up suggestions — are estimates and recommendations only. They are NOT appraisals, legal advice, financial advice, or guarantees of any kind.' },
              { title: '2. You Must Review All AI Output', text: 'You are solely responsible for reviewing, verifying, and approving all AI-generated content before sharing it with clients, using it in transactions, or acting on it in any professional capacity. AI output may contain errors, outdated information, or inappropriate recommendations.' },
              { title: '3. AI Valuations Are Not Appraisals', text: 'AI-powered property valuations and CMA reports use machine learning models trained on comparable sales data. They are statistical estimates, not licensed appraisals. They should never be represented to clients, lenders, or any party as formal appraisals.' },
              { title: '4. You Assume Professional Responsibility', text: 'As a licensed real estate professional, you assume full responsibility for all decisions made using this platform. KairosIQ AI, Inc. is not liable for losses, damages, or claims arising from reliance on AI-generated recommendations without independent professional verification.' },
              { title: '5. Data Accuracy Not Guaranteed', text: 'While we use MLS data, county records, and other sources, we do not guarantee the accuracy, completeness, or timeliness of any data or AI analysis. Market conditions change rapidly and AI models may not reflect current conditions.' },
            ].map((p, i) => (
              <div key={i} style={{ background: '#f0f7ff', borderRadius: 8, padding: 14, marginBottom: 8, borderLeft: '3px solid #1565c0' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0F1E35', marginBottom: 4 }}>{p.title}</div>
                <div style={{ fontSize: 12, color: '#555', lineHeight: 1.6 }}>{p.text}</div>
              </div>
            ))}

            <div style={{ marginTop: 20 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#0F1E35', display: 'block', marginBottom: 6 }}>
                Type your full name to acknowledge these terms
              </label>
              <input
                value={aiDisclosureName}
                onChange={e => setAiDisclosureName(e.target.value)}
                placeholder="Your full legal name"
                style={{ width: '100%', padding: '10px 14px', fontSize: 14, borderRadius: 8, border: '1px solid #ddd', outline: 'none', boxSizing: 'border-box', marginBottom: 12 }}
              />
              <button
                disabled={aiDisclosureSubmitting || aiDisclosureName.trim().length < 2}
                onClick={async () => {
                  setAiDisclosureSubmitting(true);
                  try {
                    const { data } = await axiosInstance.post('/sponsor/ai-disclosure-ack', { userId: aiDisclosureData.userId, typedName: aiDisclosureName.trim() });
                    if (data.success) setAiDisclosureRequired(false);
                  } catch { }
                  setAiDisclosureSubmitting(false);
                }}
                style={{ width: '100%', padding: '12px', fontSize: 15, fontWeight: 700, borderRadius: 8, border: 'none', cursor: aiDisclosureName.trim().length < 2 ? 'not-allowed' : 'pointer',
                  background: aiDisclosureName.trim().length < 2 ? '#ccc' : 'linear-gradient(135deg, #1565c0, #1976d2)', color: '#fff',
                  opacity: aiDisclosureSubmitting ? 0.6 : 1 }}
              >
                {aiDisclosureSubmitting ? 'Saving...' : 'I Acknowledge These Terms'}
              </button>
              <p style={{ fontSize: 10, color: '#999', textAlign: 'center', marginTop: 8, marginBottom: 0 }}>
                This acknowledgment is recorded and timestamped. You can review these terms anytime in Help & Support.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* MLS Activation Modal — $150/year */}
      {mlsRequired && !mlsDismissed && !respaRequired && !aiDisclosureRequired && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 16, maxWidth: 560, width: '100%', maxHeight: '90vh', overflowY: 'auto', padding: 0 }}>
            <div style={{ background: '#0F1E35', borderRadius: '16px 16px 0 0', padding: '32px 32px 24px', textAlign: 'center' }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg, #C9952A, #E8B44A)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, fontSize: 28 }}>🔓</div>
              <h2 style={{ color: '#fff', fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, letterSpacing: 1, margin: '0 0 8px' }}>
                Unlock Property <span style={{ color: '#C9952A' }}>Valuations</span>
              </h2>
              <p style={{ color: '#8FA3BF', fontSize: 14, margin: 0 }}>
                AI-powered price predictions for every property in your book
              </p>
            </div>
            <div style={{ padding: '24px 32px' }}>
              {mlsData?.addresses > 0 && (
                <div style={{ background: '#f0faf0', border: '1px solid #c8e6c9', borderRadius: 10, padding: 16, marginBottom: 20, textAlign: 'center' }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#0F1E35' }}>{mlsData.addresses.toLocaleString()}</div>
                  <div style={{ fontSize: 13, color: '#666' }}>property addresses in your book ready for valuation</div>
                </div>
              )}
              <div style={{ fontSize: 14, color: '#333', lineHeight: 1.7, marginBottom: 20 }}>
                <p style={{ marginBottom: 12 }}>With MLS activation, KairosIQ runs AI-powered valuations on every property address in your database:</p>
                <ul style={{ paddingLeft: 20, margin: 0 }}>
                  <li style={{ marginBottom: 6 }}><strong>Price predictions</strong> — estimated market value for every seller contact</li>
                  <li style={{ marginBottom: 6 }}><strong>Comp analysis</strong> — pulled from live MLS closed sales</li>
                  <li style={{ marginBottom: 6 }}><strong>Equity tracking</strong> — know when past clients hit equity milestones</li>
                  <li style={{ marginBottom: 6 }}><strong>Passive-seller funnel</strong> — automated alerts when properties are ready</li>
                  <li style={{ marginBottom: 6 }}><strong>Retention intelligence</strong> — see which past clients transacted with someone else</li>
                </ul>
              </div>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div style={{ fontSize: 36, fontWeight: 700, color: '#0F1E35' }}>$150<span style={{ fontSize: 16, fontWeight: 400, color: '#888' }}>/year</span></div>
                <div style={{ fontSize: 12, color: '#888' }}>Annual MLS connectivity fee — covers your ARMLS data access</div>
              </div>
              <button
                disabled={mlsLoading}
                onClick={async () => {
                  setMlsLoading(true);
                  try {
                    const { data } = await axiosInstance.post('/mls-activation/create-checkout');
                    if (data.success && data.url) {
                      window.location.href = data.url;
                    } else {
                      toast.error(data.error || 'Failed to create checkout');
                    }
                  } catch (err: any) {
                    toast.error(err.response?.data?.error || 'Checkout unavailable');
                  }
                  setMlsLoading(false);
                }}
                style={{
                  width: '100%', padding: 14, borderRadius: 10, border: 'none',
                  background: mlsLoading ? '#ccc' : 'linear-gradient(135deg, #C9952A, #E8B44A)',
                  color: '#0F1E35', fontSize: 16, fontWeight: 700, cursor: mlsLoading ? 'wait' : 'pointer',
                  marginBottom: 12
                }}
              >
                {mlsLoading ? 'Loading...' : 'Activate MLS — $150/year'}
              </button>
              <button
                onClick={() => setMlsDismissed(true)}
                style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd', background: '#fff', color: '#888', fontSize: 13, cursor: 'pointer' }}
              >
                Maybe later
              </button>
              <p style={{ fontSize: 11, color: '#aaa', textAlign: 'center', marginTop: 16, lineHeight: 1.5 }}>
                This is an annual subscription that auto-renews. You can cancel anytime.
                Your data stays private — MLS values are never shared with lenders or third parties.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Profile Setup Banner — shown when profile is incomplete */}
      {!aiDisclosureRequired && !respaRequired && portalSettings && !loading && !sessionStorage.getItem('skipProfileSetup') && (() => {
        const missing: string[] = [];
        if (!portalSettings.display_name) missing.push('Display Name');
        if (!portalSettings.brokerage_name) missing.push('Brokerage Name');
        if (!portalSettings.phone) missing.push('Phone Number');
        if (!portalSettings.email && !agentData?.email) missing.push('Email');
        if (missing.length === 0) return null;
        return (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.6)', zIndex: 10000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              background: '#fff', borderRadius: 16, padding: 32, maxWidth: 500, width: '90%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)', textAlign: 'center',
            }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#FFF3E0', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                <FontAwesomeIcon icon={faCog} style={{ fontSize: 24, color: '#E8B44A' }} />
              </div>
              <h3 style={{ fontSize: 20, fontWeight: 700, color: '#0F1E35', margin: '0 0 8px' }}>Complete Your Profile</h3>
              <p style={{ fontSize: 13, color: '#666', margin: '0 0 16px', lineHeight: 1.6 }}>
                Welcome to KairosIQ! Before you get started, please set up your profile. This information appears on your client-facing portal and communications.
              </p>
              <div style={{ background: '#f8f9fa', borderRadius: 8, padding: 12, marginBottom: 20, textAlign: 'left' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Missing Information</div>
                {missing.map((item, i) => (
                  <div key={i} style={{ fontSize: 13, color: '#c62828', padding: '3px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ color: '#c62828', fontSize: 10 }}>&#9679;</span> {item}
                  </div>
                ))}
              </div>
              <button
                onClick={() => { setActiveView('settings' as any); window.history.pushState(null, '', '/agent-portal/dashboard/settings'); }}
                style={{
                  width: '100%', padding: 14, fontSize: 15, fontWeight: 700, borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: 'linear-gradient(135deg, #C9952A, #E8B44A)', color: '#0F1E35',
                }}
              >
                Set Up Your Profile
              </button>
              <button
                onClick={() => { sessionStorage.setItem('skipProfileSetup', 'true'); window.location.reload(); }}
                style={{
                  width: '100%', padding: 10, fontSize: 13, borderRadius: 8, border: '1px solid #ddd',
                  background: 'white', color: '#888', cursor: 'pointer', marginTop: 8,
                }}
              >
                Skip for Now
              </button>
              <p style={{ fontSize: 10, color: '#999', marginTop: 8, marginBottom: 0 }}>
                You can complete your profile anytime in Settings.
              </p>
            </div>
          </div>
        );
      })()}

      {/* Mobile Menu Toggle */}
      <button
        className="mobile-menu-toggle d-lg-none"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        <FontAwesomeIcon icon={faEllipsisV} />
      </button>

      {/* Sidebar Overlay for Mobile */}
      <div 
        className={`sidebar-overlay ${sidebarOpen ? 'show' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar Navigation */}
      <div className={`modern-sidebar ${sidebarOpen ? 'open' : ''}`} style={sidebarCollapsed ? { width: '0px', minWidth: '0px', overflow: 'hidden', padding: 0 } : {}}>
        <div className="sidebar-header" style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <div className="logo-section" style={{ cursor: 'pointer' }} onClick={() => navigate('/agent-portal/dashboard')}>
            <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '22px', letterSpacing: '1px', color: 'white' }}>
              AI<span style={{ color: '#E8B44A' }}>Agent</span>Portal
            </span>
          </div>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section">
            {(() => {
              // Tab access control by subscription package
              // Lenders have their own portal (LenderSponsorHub) — they never reach this component
              const packageName = agentData?.subscriptionPackage || agentData?.subscription_package_name || '';
              const AI_PRO_TABS = ['overview', 'clients', 'communications', 'profiles', 'analytics', 'ai-coach', 'classifications', 'reports', 'marketing', 'chat', 'website', 'settings'];
              const BROKER_TABS = ['overview', 'clients', 'communications', 'documents', 'profiles', 'analytics', 'ai-coach', 'classifications', 'reports', 'marketing', 'chat', 'website', 'settings'];
              const INTERNAL_TABS = null; // null = show all (no restriction)

              const isInternal = packageName === 'InternalEmployee' || agentData?.role === 'InternalEmployee';
              const isBrokerPkg = packageName?.toLowerCase().includes('broker');
              const allowedTabs = isInternal ? INTERNAL_TABS : isBrokerPkg ? BROKER_TABS : AI_PRO_TABS;

              return [
              { view: 'overview', icon: faHome, label: 'Overview', path: '/agent-portal/dashboard', sponsorVisible: true },
              { view: 'ai-dashboard', icon: faRocket, label: 'AI Search Portal', path: '/ai-dashboard', external: true },
              { view: 'clients', icon: faUsers, label: 'My Clients', path: '/agent-portal/clients', badge: dashboardStats?.totalLeads || stats.totalClients },
              { view: 'communications', icon: faPhone, label: 'Communications', path: '/agent-portal/dashboard/communications' },
              ...(hasBrokerage ? [{ view: 'documents', icon: faFolderOpen, label: 'Documents', path: '/agent-portal/documents' }] : []),
              { view: 'profiles', icon: faSearch, label: 'Search Profiles', path: '/agent-portal/dashboard/profiles' },
              { view: 'analytics', icon: faChartLine, label: 'Analytics', path: '/agent-portal/dashboard/analytics' },
              { view: 'ai-coach', icon: faRobot, label: 'AI Coach', path: '/agent-portal/dashboard/ai-coach', badge: coachQueue.length || undefined },
              { view: 'classifications', icon: faUsers, label: 'Lead Classifications', path: '/agent-portal/dashboard/classifications' },
              { view: 'reports', icon: faFileAlt, label: 'My Reports', path: '/agent-portal/dashboard/reports' },
              { view: 'marketing', icon: faBullhorn, label: 'Marketing', path: '/agent-portal/marketing' },
              { view: 'chat', icon: faComments, label: 'Chat', path: '/agent-portal/dashboard/chat' },
              ...(isSponsor ? [{ view: 'sponsor', icon: faHandshake, label: 'Sponsor Portal', path: '/sponsor/dashboard', sponsorVisible: true }] : []),
              { view: 'website', icon: faGlobe, label: 'My Website', path: '/agent-portal/dashboard/website', sponsorVisible: true },
              { view: 'settings', icon: faCog, label: 'Settings', path: '/agent-portal/dashboard/settings', sponsorVisible: true },
            ].filter(item => (!isSponsor || item.sponsorVisible) && (allowedTabs === null || allowedTabs.includes(item.view)));
            })().map(item => (
              <a
                key={item.view}
                href={item.path}
                className={`nav-item ${activeView === item.view ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  // AI Search Portal — open simplified dashboard in new tab with auth token
                  if ((item as any).external) {
                    const token = localStorage.getItem('auth_token') || '';
                    const slug = portalSettings?.slug || '';
                    const isDevEnv = window.location.hostname.includes('dev.') || window.location.hostname === 'localhost';
                    const portalBase = isDevEnv ? 'https://dev.aisearchportal.com' : 'https://www.aisearchportal.com';
                    window.open(`${portalBase}/${slug}/agent-dashboard?token=${token}`, '_blank');
                    return;
                  }
                  // These paths are standalone pages, not internal dashboard views
                  if (
                    item.path === '/agent-portal/clients' ||
                    item.path === '/agent-portal/documents' ||
                    item.path === '/agent-portal/marketing' ||
                    item.path === '/sponsor/dashboard'
                  ) {
                    window.location.href = item.path;
                    return;
                  }
                  setActiveView(item.view as any);
                  setSidebarOpen(false);
                  window.history.pushState(null, '', item.path);
                }}
              >
                <FontAwesomeIcon icon={item.icon} />
                <span>{item.label}</span>
                {item.badge && item.badge > 0 && (
                  <Badge bg="primary" className="ms-auto">{item.badge}</Badge>
                )}
              </a>
            ))}
          </div>

          {isInternalEmployee && (
            <div className="nav-section" style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '8px', marginTop: '8px' }}>
              <div style={{ padding: '4px 20px', fontSize: '10px', fontWeight: 700, color: 'rgba(255,255,255,0.3)', letterSpacing: '1px', textTransform: 'uppercase' }}>Admin</div>
              <a href="/admin/subscribers" className="nav-item" onClick={(e) => { e.preventDefault(); window.location.href = '/admin/subscribers'; }}>
                <FontAwesomeIcon icon={faCrown} />
                <span>Subscribers</span>
              </a>
              <a href="/admin/contacts" className="nav-item" onClick={(e) => { e.preventDefault(); window.location.href = '/admin/contacts'; }}>
                <FontAwesomeIcon icon={faUsers} />
                <span>Contact Hub</span>
              </a>
              <a href="/admin/events" className="nav-item" onClick={(e) => { e.preventDefault(); window.location.href = '/admin/events'; }}>
                <FontAwesomeIcon icon={faCalendarAlt} />
                <span>Event Manager</span>
              </a>
              {/* KOPS-513 — Admin brokerage directory (our customers) */}
              <a href="/admin/brokerages" className="nav-item" onClick={(e) => { e.preventDefault(); window.location.href = '/admin/brokerages'; }}>
                <FontAwesomeIcon icon={faBuilding} />
                <span>Brokerages</span>
              </a>
              {/* KOPS-513 — MLS market analytics (entire market for sales) */}
              <a href="/admin/market-analytics" className="nav-item" onClick={(e) => { e.preventDefault(); window.location.href = '/admin/market-analytics'; }}>
                <FontAwesomeIcon icon={faChartLine} />
                <span>Market Analytics</span>
              </a>
              {/* KOPS-465 / KOPS-511 — Broker Intelligence Hub V2 (navy+gold rebuild). */}
              <a href="/broker-intelligence" className="nav-item" onClick={(e) => { e.preventDefault(); window.location.href = '/broker-intelligence'; }}>
                <FontAwesomeIcon icon={faShieldAlt} />
                <span>Broker Intelligence</span>
              </a>
              <a href="/agent-portal/county-deals" className="nav-item" onClick={(e) => { e.preventDefault(); window.location.href = '/agent-portal/county-deals'; }}>
                <FontAwesomeIcon icon={faLandmark} />
                <span>County Deals</span>
              </a>
              <a href="/admin/sponsors" className="nav-item" onClick={(e) => { e.preventDefault(); window.location.href = '/admin/sponsors'; }}>
                <FontAwesomeIcon icon={faHandshake} />
                <span>Sponsors</span>
              </a>
            </div>
          )}

          <div className="nav-section mt-auto">
            <a href="#" className={`nav-item ${activeView === 'help' ? 'active' : ''}`} onClick={(e) => { e.preventDefault(); setActiveView('help'); }}>
              <FontAwesomeIcon icon={faQuestionCircle} />
              <span>Help & Support</span>
            </a>
            <a href="/sign-out" className="nav-item" onClick={(e) => { e.preventDefault(); handleLogout(); }}>
              <FontAwesomeIcon icon={faSignOutAlt} />
              <span>Sign Out</span>
            </a>
          </div>
        </nav>

        <div className="sidebar-footer" style={{ borderTop: '1px solid rgba(255,255,255,0.1)', padding: '12px 20px' }}>
          <div className="user-info mb-2">
            <div className="user-avatar">
              {agentData?.firstName?.[0] || 'A'}{agentData?.lastName?.[0] || 'I'}
            </div>
            <div className="user-details">
              <div className="user-name">{agentData?.firstName || ''} {agentData?.lastName || ''}</div>
              <div className="user-email" style={{ fontSize: '0.75rem' }}>{agentData?.email || ''}</div>
            </div>
          </div>
          <div style={{ textAlign: 'center', opacity: 0.5, fontSize: '0.7rem', color: '#8fa3bf' }}>
            Powered by KairosIQ AI, Inc.
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="modern-main" style={sidebarCollapsed ? { marginLeft: '20px' } : {}}>
        {/* Sidebar Toggle */}
        <div
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          style={{ position: 'fixed', left: sidebarCollapsed ? 0 : '239px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '40px', background: '#0F1E35', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', borderRadius: sidebarCollapsed ? '0 6px 6px 0' : '0 6px 6px 0', zIndex: 100, transition: 'left 0.2s ease' }}
          onMouseEnter={e => { e.currentTarget.style.background = '#1a2d4a'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#0F1E35'; }}
          title={sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
        >
          <span style={{ color: '#C9952A', fontSize: '14px', fontWeight: 700 }}>{sidebarCollapsed ? '›' : '‹'}</span>
        </div>

        {/* Top Bar */}
        <div className="modern-topbar">
          <div className="topbar-left">
            <h2 className="page-title">
              {activeView === 'overview' && 'Dashboard Overview'}
              {activeView === 'clients' && 'My Customers'}
              {activeView === 'profiles' && 'Search Profiles'}
              {activeView === 'analytics' && 'Analytics'}
              {activeView === 'ai-coach' && 'AI Lead Coach'}
              {activeView === 'website' && 'My Website'}
              {activeView === 'settings' && 'Portal Settings'}
              {activeView === 'help' && 'Help & Support'}
              {activeView === 'classifications' && 'Lead Classifications'}
              {activeView === 'communications' && 'Communications'}
              {activeView === 'chat' && 'Chat'}
              {activeView === 'reports' && 'My Reports'}
            </h2>
          </div>
          <div className="topbar-right">
            <Button variant="light" size="sm" className="me-2">
              <FontAwesomeIcon icon={faBell} />
            </Button>
            <div className="d-flex gap-2">
              <Button
                size="sm"
                style={{ background: 'linear-gradient(135deg, #C9952A 0%, #E8B44A 100%)', border: 'none', fontWeight: 600 }}
                onClick={() => navigate('/agent-portal/clients?add=true')}
              >
                <FontAwesomeIcon icon={faUserPlus} className="me-2" />
                Add Client
              </Button>
              <Button
                size="sm"
                variant="outline-light"
                onClick={() => startDiscoveryPolling()}
              >
                <FontAwesomeIcon icon={faSearch} className="me-2" />
                Discover Clients
              </Button>
              <Button
                variant="outline-light"
                size="sm"
                onClick={() => setShowInviteModal(true)}
              >
                <FontAwesomeIcon icon={faLink} className="me-2" />
                Invite to Portal
              </Button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="modern-content">
          {/* Overview View */}
          {activeView === 'overview' && (
            <>
              {/* Portal Status Card */}
              <Card className="portal-status-card mb-4">
                <Card.Body>
                  <Row className="align-items-center">
                    <Col>
                      <h5 className="mb-1">Your AI Agent Portal</h5>
                      <div className="portal-url">
                        <FontAwesomeIcon icon={faGlobe} className="me-2" />
                        <a href={agentData?.portalUrl} target="_blank" rel="noopener noreferrer">
                          {agentData?.portalUrl}
                        </a>
                        <Button 
                          variant="link" 
                          size="sm"
                          onClick={copyPortalLink}
                          className="ms-2"
                        >
                          <FontAwesomeIcon icon={linkCopied ? faCheckCircle : faCopy} />
                        </Button>
                      </div>
                    </Col>
                    <Col xs="auto">
                      <div className="trial-status">
                        {agentData?.subscriptionStatus === 'trialing' ? (
                          <>
                            <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
                            <span>Trial ends: {new Date(agentData?.trialEndsAt).toLocaleDateString()}</span>
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faCreditCard} className="me-2" />
                            <span>Active Subscription</span>
                          </>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* AI-Powered Stats Grid */}
              <Row className="stats-grid mb-4">
                <Col md={3}>
                  <Card className="stat-card" style={{ borderLeft: '4px solid #6366f1' }}>
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <div className="stat-value" style={{ fontSize: '2rem', fontWeight: 700, color: '#6366f1' }}>{(dashboardStats?.totalLeads || stats.totalClients || 0).toLocaleString()}</div>
                          <div className="stat-label fw-semibold">Total Clients</div>
                        </div>
                        <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 40, height: 40, background: 'rgba(99,102,241,0.1)' }}>
                          <FontAwesomeIcon icon={faUsers} style={{ color: '#6366f1' }} />
                        </div>
                      </div>
                      <div className="mt-2"><small className="text-muted">{dashboardStats?.buyerProfiles || 0} buyers · {dashboardStats?.sellerProfiles || 0} sellers</small></div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="stat-card" style={{ borderLeft: '4px solid #10b981' }}>
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <div className="stat-value" style={{ fontSize: '2rem', fontWeight: 700, color: '#10b981' }}>{dashboardStats?.totalCMAReports || 0}</div>
                          <div className="stat-label fw-semibold">AI CMA Reports</div>
                        </div>
                        <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 40, height: 40, background: 'rgba(16,185,129,0.1)' }}>
                          <FontAwesomeIcon icon={faChartLine} style={{ color: '#10b981' }} />
                        </div>
                      </div>
                      <div className="mt-2"><small className="text-success">{dashboardStats?.cmaReportsThisMonth || 0} this month</small></div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="stat-card" style={{ borderLeft: '4px solid #f59e0b' }}>
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <div className="stat-value" style={{ fontSize: '2rem', fontWeight: 700, color: '#f59e0b' }}>{dashboardStats?.activeThisWeek || 0}</div>
                          <div className="stat-label fw-semibold">Active This Week</div>
                        </div>
                        <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 40, height: 40, background: 'rgba(245,158,11,0.1)' }}>
                          <FontAwesomeIcon icon={faBolt} style={{ color: '#f59e0b' }} />
                        </div>
                      </div>
                      <div className="mt-2"><small className="text-muted">{dashboardStats?.totalLeads ? Math.round((dashboardStats.activeThisWeek / dashboardStats.totalLeads) * 100) : 0}% engagement rate</small></div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="stat-card" style={{ borderLeft: '4px solid #ef4444' }}>
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <div className="stat-value" style={{ fontSize: '2rem', fontWeight: 700, color: '#ef4444' }}>{dashboardStats?.newLeadsThisMonth || 0}</div>
                          <div className="stat-label fw-semibold">New This Month</div>
                        </div>
                        <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 40, height: 40, background: 'rgba(239,68,68,0.1)' }}>
                          <FontAwesomeIcon icon={faUserPlus} style={{ color: '#ef4444' }} />
                        </div>
                      </div>
                      <div className="mt-2"><small className="text-muted">New leads added</small></div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Quick Actions + Recent Activity */}
              <Row className="mb-4">
                <Col md={4}>
                  <Card className="h-100">
                    <Card.Header className="bg-white"><h6 className="mb-0"><FontAwesomeIcon icon={faRocket} className="me-2 text-primary" />Quick Actions</h6></Card.Header>
                    <Card.Body className="d-grid gap-2">
                      <Button variant="outline-primary" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/${dashboardStats?.subdomain || ''}`); toast.success('Buyer portal link copied!'); }}>
                        <FontAwesomeIcon icon={faGlobe} className="me-2" />Copy Buyer Portal Link
                      </Button>
                      <Button variant="outline-success" onClick={() => { setActiveView('clients' as any); window.history.pushState(null, '', '/agent-portal/dashboard/customers'); }}>
                        <FontAwesomeIcon icon={faUsers} className="me-2" />View My Customers
                      </Button>
                      <Button variant="outline-info" onClick={() => { setActiveView('settings' as any); window.history.pushState(null, '', '/agent-portal/dashboard/settings'); }}>
                        <FontAwesomeIcon icon={faCog} className="me-2" />Portal Settings
                      </Button>
                      {isInternalEmployee && (
                        <>
                          <hr className="my-2" />
                          <Button variant="outline-dark" onClick={() => window.location.href = '/admin/subscribers'}>
                            <FontAwesomeIcon icon={faCrown} className="me-2" />Subscribers
                          </Button>
                          <Button variant="outline-dark" onClick={() => window.location.href = '/admin/contacts'}>
                            <FontAwesomeIcon icon={faUsers} className="me-2" />Contact Hub
                          </Button>
                          <Button variant="outline-dark" onClick={() => window.location.href = '/admin/events'}>
                            <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />Event Manager
                          </Button>
                          <Button variant="outline-dark" onClick={() => window.location.href = '/broker-intelligence-v2'}>
                            <FontAwesomeIcon icon={faShieldAlt} className="me-2" />Broker Intelligence
                          </Button>
                        </>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
                {/* AI Coach Summary Card */}
                <Col md={12} className="mb-3">
                  <Card style={{ borderLeft: '4px solid #C9952A' }}>
                    <Card.Body className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        <div style={{ width: 48, height: 48, borderRadius: '12px', background: 'linear-gradient(135deg, #C9952A 0%, #E8B44A 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 16 }}>
                          <FontAwesomeIcon icon={faRobot} style={{ color: 'white', fontSize: '1.3rem' }} />
                        </div>
                        <div>
                          <h6 style={{ margin: 0, fontWeight: 700 }}>
                            {coachQueue.length > 0
                              ? `Jasper has ${coachQueue.length} draft${coachQueue.length === 1 ? '' : 's'} ready for you`
                              : 'AI Lead Coach ready'
                            }
                          </h6>
                          <small className="text-muted">
                            {coachQueue.length > 0
                              ? 'Review and send personalized follow-ups'
                              : 'Generate smart follow-ups for your leads'
                            }
                          </small>
                        </div>
                      </div>
                      <Button
                        onClick={() => { setActiveView('ai-coach'); fetchCoachQueue(); fetchColdLeads(); fetchUpcomingTasks(); fetchUrgentAlerts(); }}
                        style={{ background: 'linear-gradient(135deg, #C9952A 0%, #E8B44A 100%)', border: 'none', fontWeight: 600 }}
                      >
                        {coachQueue.length > 0 ? 'Review Drafts' : 'Open AI Coach'}
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={8}>
                  <Card className="h-100">
                    <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                      <h6 className="mb-0"><FontAwesomeIcon icon={faChartLine} className="me-2 text-success" />Recent AI CMA Reports</h6>
                    </Card.Header>
                    <Card.Body className="p-0">
                      {(dashboardStats?.recentReports || []).length > 0 ? (
                        <div className="list-group list-group-flush">
                          {(dashboardStats?.recentReports || []).map((report: any) => (
                            <div key={report.id} className="list-group-item d-flex justify-content-between align-items-center">
                              <div>
                                <div className="fw-semibold">{report.title}</div>
                                <small className="text-muted">{new Date(report.createdAt).toLocaleDateString()} · {report.comparableCount || 0} comps</small>
                              </div>
                              <div className="text-end">
                                {report.predictedValue > 0 && (
                                  <span className="fw-bold text-success">${Math.round(report.predictedValue).toLocaleString()}</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4 text-muted">
                          <FontAwesomeIcon icon={faChartLine} size="2x" className="mb-2 opacity-50" />
                          <p className="mb-0">No CMA reports yet. Search a property address to generate one.</p>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Recent Leads */}
              <Row className="stats-grid mb-4">
                <Col md={3}>
                  <Card className="stat-card">
                    <Card.Body>
                      <div className="stat-value">{stats.newLeads}</div>
                      <div className="stat-label">New Leads</div>
                      <div className="stat-trend">
                        <FontAwesomeIcon icon={faUserPlus} className="me-1" />
                        <span className="text-muted">This month</span>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Recent Activity */}
              <Card className="activity-card">
                <Card.Header>
                  <h5 className="mb-0">Recent Customer Leads</h5>
                </Card.Header>
                <Card.Body>
                  <div className="activity-list">
                    {crmLeads.length > 0 ? (
                      crmLeads.slice(0, 5).map(lead => {
                        const leadName = `${lead.lead_first_name || ''} ${lead.lead_last_name || ''}`.trim() || 'Unknown';
                        return (
                          <div key={lead.id} className="activity-item">
                            <div className="activity-icon">
                              <FontAwesomeIcon icon={faUsers} />
                            </div>
                            <div className="activity-content">
                              <div className="activity-title">{leadName}</div>
                              <div className="activity-meta">
                                {lead.lead_email || lead.lead_phone || 'No contact info'} • {lead.lead_source || 'Direct'}
                              </div>
                            </div>
                            <div className="activity-badge">
                              <Badge bg={
                                lead.status === 'New' ? 'primary' : 
                                lead.status === 'Contacted' ? 'info' :
                                lead.status === 'Qualified' ? 'success' :
                                lead.status === 'Working' ? 'warning' : 'secondary'
                              }>
                                {lead.status || 'New'}
                              </Badge>
                            </div>
                          </div>
                        );
                      })
                    ) : clients.slice(0, 5).map(client => (
                      <div key={client.id} className="activity-item">
                        <div className="activity-icon">
                          <FontAwesomeIcon icon={faUsers} />
                        </div>
                        <div className="activity-content">
                          <div className="activity-title">{client.name}</div>
                          <div className="activity-meta">
                            {client.status === 'active' ? 'Last active ' : 'Invited on '}
                            {client.lastActive}
                          </div>
                        </div>
                        <div className="activity-badge">
                          <Badge bg={client.status === 'active' ? 'success' : 'warning'}>
                            {client.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </>
          )}

          {/* Clients View */}
          {activeView === 'clients' && (
            <Card className="clients-card">
              <Card.Header>
                <Row className="align-items-center">
                  <Col>
                    <div className="search-bar">
                      <FontAwesomeIcon icon={faSearch} className="search-icon" />
                      <Form.Control
                        type="text"
                        placeholder="Search by name, email, or phone..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="search-input"
                      />
                    </div>
                  </Col>
                  <Col xs="auto">
                    <Dropdown>
                      <Dropdown.Toggle variant="light" size="sm">
                        <FontAwesomeIcon icon={faFilter} className="me-2" />
                        {filterStatus === 'all' ? 'All Clients' : filterStatus}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => setFilterStatus('all')}>All Clients</Dropdown.Item>
                        <Dropdown.Item onClick={() => setFilterStatus('active')}>Active</Dropdown.Item>
                        <Dropdown.Item onClick={() => setFilterStatus('invited')}>Invited</Dropdown.Item>
                        <Dropdown.Item onClick={() => setFilterStatus('inactive')}>Inactive</Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </Col>
                </Row>
              </Card.Header>
              <Card.Body className="p-0">
                {loadingClients ? (
                  <div className="d-flex justify-content-center align-items-center p-5">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading customers...</span>
                    </Spinner>
                  </div>
                ) : filteredLeads.length === 0 ? (
                  <div className="empty-state">
                    <FontAwesomeIcon icon={faUsers} size="3x" className="text-muted mb-3" />
                    <h5>No customer leads found</h5>
                    <p className="text-muted">
                      {searchQuery ? 'Try adjusting your search criteria' : 'Customer leads from your CRM will appear here'}
                    </p>
                    {!searchQuery && (
                      <Button variant="primary" onClick={() => setShowInviteModal(true)}>
                        <FontAwesomeIcon icon={faUserPlus} className="me-2" />
                        Invite Client
                      </Button>
                    )}
                  </div>
                ) : (
                  <Table hover responsive className="clients-table mb-0">
                    <thead>
                      <tr>
                        <th>Customer Name</th>
                        <th>Contact Info</th>
                        <th>Source</th>
                        <th>Price Range</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLeads.map(lead => {
                        const leadName = `${lead.lead_first_name || ''} ${lead.lead_last_name || ''}`.trim() || 'Unknown';
                        const initials = leadName !== 'Unknown' 
                          ? leadName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                          : '?';
                        
                        // Find profile info for this lead
                        const profile = searchProfiles?.profiles?.find((p: any) => p.lead.id === lead.id);
                        const hasPortalAccess = profile?.lead?.has_portal_access || false;
                        const profileType = profile?.profile_type || 'none';
                        
                        return (
                          <tr key={lead.id}>
                            <td>
                              <div className="client-name" 
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                  navigate(`/agent-portal/client/${lead.id}`);
                                }}
                              >
                                <div className="client-avatar">
                                  {initials}
                                </div>
                                <div>
                                  <span className="text-primary">{leadName}</span>
                                  <div className="small text-muted">
                                    {profileType !== 'none' && (
                                      <Badge bg="info" className="me-1" style={{fontSize: '0.7rem'}}>
                                        {profileType === 'both' ? 'Buyer & Seller' : profileType}
                                      </Badge>
                                    )}
                                    {hasPortalAccess && (
                                      <Badge bg="success" style={{fontSize: '0.7rem'}}>
                                        <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                        Portal Active
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div>
                                {lead.lead_email && <div className="small">{lead.lead_email}</div>}
                                {lead.lead_phone && <div className="small text-muted">{lead.lead_phone}</div>}
                                {!lead.lead_email && !lead.lead_phone && <span className="text-muted">No contact info</span>}
                              </div>
                            </td>
                            <td>{lead.lead_source || 'Direct'}</td>
                          <td>{lead.lead_price_range || 'Not specified'}</td>
                          <td>
                            <Badge 
                              bg={
                                lead.status === 'New' ? 'primary' : 
                                lead.status === 'Contacted' ? 'info' :
                                lead.status === 'Qualified' ? 'success' :
                                lead.status === 'Working' ? 'warning' :
                                lead.status === 'Lost' ? 'danger' : 'secondary'
                              }
                            >
                              {lead.status}
                            </Badge>
                          </td>
                          <td>{new Date(lead.created_on).toLocaleDateString()}</td>
                          <td>
                            <Dropdown>
                              <Dropdown.Toggle variant="link" size="sm" className="action-btn">
                                <FontAwesomeIcon icon={faEllipsisV} />
                              </Dropdown.Toggle>
                              <Dropdown.Menu>
                                <Dropdown.Item>
                                  <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                                  Send Email
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => window.open(`/leads/${lead.id}?type=ai+assistant`, '_blank')}>
                                  <FontAwesomeIcon icon={faChartLine} className="me-2" />
                                  View in CRM
                                </Dropdown.Item>
                                {profileType === 'seller' || profileType === 'both' ? (
                                  <Dropdown.Item onClick={() => {
                                    // Navigate to SmartCMA workflow with lead data
                                    window.open(`/smart-cma/workflow?leadId=${lead.id}&address=${encodeURIComponent(lead.lead_city || '')}`, '_blank');
                                  }}>
                                    <FontAwesomeIcon icon={faFileInvoiceDollar} className="me-2" />
                                    Generate CMA Report
                                  </Dropdown.Item>
                                ) : null}
                                {!hasPortalAccess ? (
                                  <Dropdown.Item onClick={async () => {
                                    try {
                                      const result = await agentPortalService.enablePortalAccess(lead.id);
                                      if (result.success) {
                                        alert(`Portal access enabled! Link: ${result.data.portal_link}`);
                                        // Refresh data
                                        fetchDashboardData();
                                      }
                                    } catch (error) {
                                      alert('Failed to enable portal access');
                                    }
                                  }}>
                                    <FontAwesomeIcon icon={faUserPlus} className="me-2" />
                                    Enable Portal Access
                                  </Dropdown.Item>
                                ) : (
                                  <Dropdown.Item onClick={() => {
                                    const isStaging = window.location.hostname.includes('dev.') || window.location.hostname === 'localhost';
                                    const base = isStaging ? 'https://dev.aisearchportal.com' : 'https://www.aisearchportal.com';
                                    const slug = (window as any).__agentPortalSlug || 'portal';
                                    const portalLink = `${base}/${slug}?client=${profile?.lead?.id}`;
                                    navigator.clipboard.writeText(portalLink);
                                    alert('Portal link copied to clipboard!');
                                  }}>
                                    <FontAwesomeIcon icon={faLink} className="me-2" />
                                    Copy Portal Link
                                  </Dropdown.Item>
                                )}
                                <Dropdown.Divider />
                                <Dropdown.Item className="text-danger">
                                  <FontAwesomeIcon icon={faArchive} className="me-2" />
                                  Archive
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          )}

          {/* Search Profiles View */}
          {activeView === 'profiles' && (
            <div className="search-profiles-view">
              {loadingProfiles ? (
                <div className="d-flex justify-content-center align-items-center p-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading search profiles...</span>
                  </Spinner>
                </div>
              ) : searchProfiles?.profiles?.length > 0 ? (
                <>
                  {/* Profile Stats */}
                  <Row className="mb-4">
                    <Col md={3}>
                      <Card className="stat-card">
                        <Card.Body>
                          <div className="stat-value">{searchProfiles.stats.total_leads}</div>
                          <div className="stat-label">Total Leads</div>
                          <div className="stat-icon">
                            <FontAwesomeIcon icon={faUsers} />
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card className="stat-card">
                        <Card.Body>
                          <div className="stat-value">{searchProfiles.stats.buyers}</div>
                          <div className="stat-label">Buyer Profiles</div>
                          <div className="stat-icon">
                            <FontAwesomeIcon icon={faHome} />
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card className="stat-card">
                        <Card.Body>
                          <div className="stat-value">{searchProfiles.stats.search_ready}</div>
                          <div className="stat-label">Search Ready</div>
                          <div className="stat-icon">
                            <FontAwesomeIcon icon={faCheckCircle} />
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card className="stat-card">
                        <Card.Body>
                          <div className="stat-value">{searchProfiles.stats.portal_enabled}</div>
                          <div className="stat-label">Portal Active</div>
                          <div className="stat-icon">
                            <FontAwesomeIcon icon={faRobot} />
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  {/* Search Profiles Table */}
                  <Card className="profiles-card">
                    <Card.Header>
                      <h5 className="mb-0">Customer Search Profiles</h5>
                    </Card.Header>
                    <Card.Body className="p-0">
                      <Table hover responsive className="profiles-table mb-0">
                        <thead>
                          <tr>
                            <th>Customer</th>
                            <th>Profile Type</th>
                            <th>Search Preferences</th>
                            <th>AI Score</th>
                            <th>Portal Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {searchProfiles.profiles.map((profile: any) => (
                            <tr key={profile.lead.id}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className="client-avatar me-2">
                                    {profile.lead.name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)}
                                  </div>
                                  <div>
                                    <div>{profile.lead.name}</div>
                                    <small className="text-muted">{profile.lead.email || 'No email'}</small>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <Badge bg={
                                  profile.profile_type === 'both' ? 'primary' :
                                  profile.profile_type === 'buyer' ? 'info' :
                                  profile.profile_type === 'seller' ? 'warning' : 'secondary'
                                }>
                                  {profile.profile_type === 'both' ? 'Buyer & Seller' :
                                   profile.profile_type === 'buyer' ? 'Buyer' :
                                   profile.profile_type === 'seller' ? 'Seller' : 'None'}
                                </Badge>
                              </td>
                              <td>
                                {profile.buyer_profile ? (
                                  <div>
                                    <small className="d-block">
                                      <strong>Budget:</strong> ${profile.buyer_profile.pre_approval_amount?.toLocaleString() || 'Not set'}
                                    </small>
                                    <small className="text-muted">
                                      <strong>Timeline:</strong> {profile.buyer_profile.timeframe || 'Not specified'}
                                    </small>
                                  </div>
                                ) : (
                                  <span className="text-muted">No preferences set</span>
                                )}
                              </td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className="progress" style={{ width: '80px', height: '8px' }}>
                                    <div 
                                      className="progress-bar" 
                                      role="progressbar" 
                                      style={{ width: `${profile.ai_context.completeness_score}%` }}
                                      aria-valuenow={profile.ai_context.completeness_score}
                                      aria-valuemin={0}
                                      aria-valuemax={100}
                                    />
                                  </div>
                                  <span className="ms-2">{profile.ai_context.completeness_score}%</span>
                                </div>
                              </td>
                              <td>
                                {profile.lead.has_portal_access ? (
                                  <Badge bg="success">
                                    <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                    Active
                                  </Badge>
                                ) : (
                                  <Badge bg="secondary">Inactive</Badge>
                                )}
                              </td>
                              <td>
                                <Dropdown>
                                  <Dropdown.Toggle variant="link" size="sm" className="action-btn">
                                    <FontAwesomeIcon icon={faEllipsisV} />
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu>
                                    {profile.buyer_profile && (
                                      <Dropdown.Item onClick={() => window.open(`/leads/${profile.lead.id}?type=ai+assistant`, '_blank')}>
                                        <FontAwesomeIcon icon={faCog} className="me-2" />
                                        Edit Preferences
                                      </Dropdown.Item>
                                    )}
                                    {!profile.buyer_profile && (
                                      <Dropdown.Item onClick={() => window.open(`/leads/${profile.lead.id}?type=ai+assistant`, '_blank')}>
                                        <FontAwesomeIcon icon={faUserPlus} className="me-2" />
                                        Create Profile
                                      </Dropdown.Item>
                                    )}
                                    {!profile.lead.has_portal_access && (
                                      <Dropdown.Item onClick={async () => {
                                        try {
                                          const result = await agentPortalService.enablePortalAccess(profile.lead.id);
                                          if (result.success) {
                                            alert(`Portal access enabled! Link: ${result.data.portal_link}`);
                                            fetchDashboardData();
                                          }
                                        } catch (error) {
                                          alert('Failed to enable portal access');
                                        }
                                      }}>
                                        <FontAwesomeIcon icon={faRobot} className="me-2" />
                                        Enable Portal
                                      </Dropdown.Item>
                                    )}
                                    {profile.lead.has_portal_access && (
                                      <Dropdown.Item onClick={() => {
                                        const isStg = window.location.hostname.includes('dev.') || window.location.hostname === 'localhost';
                                        const b = isStg ? 'https://dev.aisearchportal.com' : 'https://www.aisearchportal.com';
                                        const s = (window as any).__agentPortalSlug || 'portal';
                                        const portalLink = `${b}/${s}?client=${profile.lead.id}`;
                                        navigator.clipboard.writeText(portalLink);
                                        alert('Portal link copied!');
                                      }}>
                                        <FontAwesomeIcon icon={faLink} className="me-2" />
                                        Copy Portal Link
                                      </Dropdown.Item>
                                    )}
                                    <Dropdown.Divider />
                                    <Dropdown.Item onClick={() => {
                                      navigate(`/agent-portal/client/${profile.lead.id}`);
                                    }}>
                                      <FontAwesomeIcon icon={faExternalLinkAlt} className="me-2" />
                                      View Details
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </>
              ) : (
                <div className="empty-state">
                  <FontAwesomeIcon icon={faSearch} size="3x" className="text-muted mb-3" />
                  <h5>No Search Profiles Yet</h5>
                  <p className="text-muted">
                    Start creating buyer profiles for your customers to enable AI-powered home search
                  </p>
                  <Button 
                    variant="primary" 
                    onClick={() => setActiveView('clients')}
                  >
                    <FontAwesomeIcon icon={faUsers} className="me-2" />
                    View Customers
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Analytics View */}
          {activeView === 'analytics' && (
            <div className="analytics-view">
              <Alert variant="info">
                <FontAwesomeIcon icon={faChartLine} className="me-2" />
                Advanced analytics coming soon! Track client engagement, popular searches, and conversion metrics.
              </Alert>
              
              {/* Quick Stats */}
              <Row className="mb-4">
                <Col md={6}>
                  <Card>
                    <Card.Body>
                      <h5>Search Patterns</h5>
                      <div className="stat-display">
                        <div className="stat-large">{stats.averageSearchesPerClient}</div>
                        <div className="stat-label">Average searches per client</div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card>
                    <Card.Body>
                      <h5>Engagement Rate</h5>
                      <div className="stat-display">
                        <div className="stat-large">
                          {stats.totalClients > 0 ? Math.round((stats.activeThisWeek / stats.totalClients) * 100) : 0}%
                        </div>
                        <div className="stat-label">Clients active this week</div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          )}

          {/* AI Coach View */}
          {activeView === 'ai-coach' && !hasAIPro && (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <FontAwesomeIcon icon={faRobot} size="3x" style={{ color: '#C9952A', opacity: 0.4, marginBottom: '16px' }} />
              <h4 style={{ fontWeight: 700, color: '#0F1E35' }}>Upgrade to AI Pro</h4>
              <p style={{ color: '#666', fontSize: '15px', maxWidth: '500px', margin: '0 auto 24px' }}>
                AI Lead Coach analyzes every lead, drafts personalized follow-ups, builds 30-day action plans, and responds to new leads instantly — all from your own email.
              </p>
              <Button onClick={() => navigate('/pricing')} style={{ background: 'linear-gradient(135deg, #C9952A 0%, #E8B44A 100%)', border: 'none', fontWeight: 700, padding: '14px 36px', fontSize: '16px' }}>
                Upgrade to AI Pro — $185/mo
              </Button>
              <div style={{ fontSize: '12px', color: '#999', marginTop: '12px' }}>Current plan: {userPlan.replace(/-/g, ' ')}</div>
            </div>
          )}
          {activeView === 'ai-coach' && hasAIPro && (
            <div className="ai-coach-view">
              {/* Header with generate button */}
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h5 style={{ fontWeight: 700, margin: 0 }}>
                    <FontAwesomeIcon icon={faRobot} className="me-2" style={{ color: '#C9952A' }} />
                    AI Lead Coach
                  </h5>
                  <small className="text-muted">Jasper analyzes your leads and drafts personalized follow-ups</small>
                </div>
                <Button
                  onClick={handleGenerateDrafts}
                  disabled={generatingDrafts}
                  style={{ background: 'linear-gradient(135deg, #C9952A 0%, #E8B44A 100%)', border: 'none', fontWeight: 600, padding: '10px 24px' }}
                >
                  {generatingDrafts ? (
                    <><Spinner size="sm" className="me-2" /> Analyzing leads...</>
                  ) : (
                    <><FontAwesomeIcon icon={faBolt} className="me-2" /> Generate Follow-Ups</>
                  )}
                </Button>
              </div>

              {/* URGENT Property Alerts — Foreclosures & Death Deeds */}
              {urgentAlerts.length > 0 && (() => {
                const [urgentFilter, setUrgentFilter] = [
                  (window as any).__urgentFilter || 'all',
                  (v: string) => { (window as any).__urgentFilter = v; setUrgentAlerts([...urgentAlerts]); }
                ];
                const foreclosureCount = urgentAlerts.filter((t: any) => { const d = typeof t.task_data === 'string' ? JSON.parse(t.task_data) : t.task_data || {}; return d.type === 'foreclosure'; }).length;
                const deathCount = urgentAlerts.length - foreclosureCount;
                const filtered = urgentFilter === 'all' ? urgentAlerts : urgentAlerts.filter((t: any) => {
                  const d = typeof t.task_data === 'string' ? JSON.parse(t.task_data) : t.task_data || {};
                  return urgentFilter === 'foreclosure' ? d.type === 'foreclosure' : d.type !== 'foreclosure';
                });

                return (
                <Card className="mb-4" style={{ border: '2px solid #c62828', borderLeft: '4px solid #c62828' }}>
                  <Card.Header style={{ background: '#fce4ec' }}>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 style={{ margin: 0, fontWeight: 700, color: '#c62828' }}>
                          🚨 Urgent Property Alerts ({urgentAlerts.length})
                        </h6>
                        <small style={{ color: '#666' }}>Foreclosures and estate changes — immediate action recommended</small>
                      </div>
                      <div className="d-flex gap-2">
                        <button onClick={() => setUrgentFilter('all')} style={{ padding: '4px 10px', fontSize: '11px', fontWeight: 600, borderRadius: '12px', border: urgentFilter === 'all' ? '2px solid #c62828' : '1px solid #ddd', background: urgentFilter === 'all' ? '#c62828' : 'white', color: urgentFilter === 'all' ? 'white' : '#666', cursor: 'pointer' }}>All ({urgentAlerts.length})</button>
                        <button onClick={() => setUrgentFilter('foreclosure')} style={{ padding: '4px 10px', fontSize: '11px', fontWeight: 600, borderRadius: '12px', border: urgentFilter === 'foreclosure' ? '2px solid #ff5722' : '1px solid #ddd', background: urgentFilter === 'foreclosure' ? '#ff5722' : 'white', color: urgentFilter === 'foreclosure' ? 'white' : '#666', cursor: 'pointer' }}>Foreclosure ({foreclosureCount})</button>
                        <button onClick={() => setUrgentFilter('death')} style={{ padding: '4px 10px', fontSize: '11px', fontWeight: 600, borderRadius: '12px', border: urgentFilter === 'death' ? '2px solid #e65100' : '1px solid #ddd', background: urgentFilter === 'death' ? '#e65100' : 'white', color: urgentFilter === 'death' ? 'white' : '#666', cursor: 'pointer' }}>Death Certificate ({deathCount})</button>
                      </div>
                    </div>
                  </Card.Header>
                  <Card.Body style={{ padding: 0, maxHeight: '500px', overflowY: 'auto' }}>
                    {filtered.map((task: any) => {
                      const taskData = typeof task.task_data === 'string' ? JSON.parse(task.task_data) : task.task_data || {};
                      const isForeclosure = taskData.type === 'foreclosure';
                      return (
                        <div key={task.task_id} style={{ padding: '12px 20px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
                          onClick={() => { window.location.href = `/agent-portal/clients?id=${task.lead_id}&tab=overview`; }}
                          onMouseEnter={e => { e.currentTarget.style.background = '#fff5f5'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'white'; }}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 700, fontSize: '14px', color: '#0F1E35' }}>
                                {isForeclosure ? '⚠️' : '🏠'} {task.lead_first_name} {task.lead_last_name}
                                <span style={{ fontSize: '10px', fontWeight: 600, padding: '2px 6px', borderRadius: '4px', marginLeft: '8px', background: isForeclosure ? '#ffebee' : '#fff3e0', color: isForeclosure ? '#c62828' : '#e65100' }}>
                                  {isForeclosure ? 'FORECLOSURE' : 'DEATH CERTIFICATE'}
                                </span>
                              </div>
                              <div style={{ fontSize: '12px', color: '#666', marginTop: '3px' }}>{taskData.address || taskData.reason?.substring(0, 80)}</div>
                              <div style={{ display: 'flex', gap: '12px', marginTop: '3px' }}>
                                {task.lead_email && <span style={{ fontSize: '11px', color: '#999' }}>{task.lead_email}</span>}
                                {task.lead_phone && <span style={{ fontSize: '11px', color: '#C9952A', fontWeight: 600 }}>📞 {task.lead_phone}</span>}
                              </div>
                            </div>
                            <Button size="sm" variant="outline-secondary" style={{ fontSize: '10px', whiteSpace: 'nowrap' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                axiosInstance.post(`/ai-coach/upcoming/${task.task_id}/cancel`, { reason: 'Reviewed by agent' }).then(() => {
                                  setUrgentAlerts(prev => prev.filter(a => a.task_id !== task.task_id));
                                }).catch(() => {});
                              }}
                            >
                              Dismiss
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </Card.Body>
                </Card>
                );
              })()}

              {/* Pending Drafts */}
              {coachQueue.length > 0 && (
                <Card className="mb-4">
                  <Card.Header className="bg-white">
                    <h6 style={{ margin: 0, fontWeight: 700 }}>
                      <FontAwesomeIcon icon={faPaperPlane} className="me-2" style={{ color: '#C9952A' }} />
                      Drafts Ready to Send ({coachQueue.length})
                    </h6>
                  </Card.Header>
                  <Card.Body style={{ padding: 0 }}>
                    {coachQueue.map((item: any) => {
                      const isEditing = editingDraft === item.id;
                      const context = typeof item.context === 'string' ? JSON.parse(item.context) : item.context;
                      return (
                        <div key={item.id} style={{ padding: '20px', borderBottom: '1px solid #f0f0f0' }}>
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div>
                              <div style={{ fontWeight: 700, fontSize: '15px' }}>
                                <a href={`/agent-portal/clients?id=${item.lead_id}&tab=overview`} style={{ color: '#0F1E35', textDecoration: 'none' }}
                                  onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; e.currentTarget.style.color = '#C9952A'; }}
                                  onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; e.currentTarget.style.color = '#0F1E35'; }}
                                >{item.lead_first_name} {item.lead_last_name}</a>
                              </div>
                              <small className="text-muted">
                                {item.lead_email}
                                {context?.leadType && <Badge bg={context.leadType === 'seller' ? 'warning' : 'info'} className="ms-2">{context.leadType}</Badge>}
                                {context?.reason && <span className="ms-2">— {context.reason}</span>}
                              </small>
                            </div>
                            <small className="text-muted">{new Date(item.created_at).toLocaleDateString()}</small>
                          </div>

                          {isEditing ? (
                            <div className="mb-3">
                              <input
                                value={editSubject}
                                onChange={e => setEditSubject(e.target.value)}
                                className="form-control mb-2"
                                style={{ fontWeight: 600 }}
                              />
                              <textarea
                                value={editBody}
                                onChange={e => setEditBody(e.target.value)}
                                className="form-control"
                                rows={6}
                                style={{ fontSize: '14px' }}
                              />
                            </div>
                          ) : (
                            <div className="mb-3 p-3 rounded" style={{ background: '#f8f9fa', border: '1px solid #e9ecef' }}>
                              <div style={{ fontWeight: 600, marginBottom: '8px', color: '#333' }}>
                                Subject: {item.subject}
                              </div>
                              <div style={{ fontSize: '14px', color: '#555', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                                {item.body}
                              </div>
                            </div>
                          )}

                          <div className="d-flex gap-2">
                            {isEditing ? (
                              <>
                                <Button size="sm" variant="outline-secondary" onClick={() => setEditingDraft(null)}>Cancel</Button>
                                <Button size="sm"
                                  style={{ background: 'linear-gradient(135deg, #C9952A 0%, #E8B44A 100%)', border: 'none', fontWeight: 600 }}
                                  onClick={() => handleSendDraft(item.id)}
                                  disabled={sendingDraft === item.id}
                                >
                                  {sendingDraft === item.id ? <Spinner size="sm" /> : 'Send Edited'}
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button size="sm" variant="outline-primary" onClick={() => {
                                  setEditingDraft(item.id);
                                  setEditSubject(item.subject);
                                  setEditBody(item.body);
                                }}>
                                  Edit
                                </Button>
                                <Button size="sm"
                                  style={{ background: 'linear-gradient(135deg, #C9952A 0%, #E8B44A 100%)', border: 'none', fontWeight: 600 }}
                                  onClick={() => handleSendDraft(item.id)}
                                  disabled={sendingDraft === item.id}
                                >
                                  {sendingDraft === item.id ? <Spinner size="sm" /> : <><FontAwesomeIcon icon={faPaperPlane} className="me-1" /> Send</>}
                                </Button>
                                <Button size="sm" variant="outline-secondary" onClick={() => handleDismissDraft(item.id)}>
                                  Dismiss
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </Card.Body>
                </Card>
              )}

              {/* Empty state */}
              {coachQueue.length === 0 && !generatingDrafts && (
                <Card className="mb-4">
                  <Card.Body className="text-center py-5">
                    <FontAwesomeIcon icon={faRobot} size="3x" style={{ color: '#C9952A', opacity: 0.5 }} className="mb-3" />
                    <h5 style={{ fontWeight: 600, color: '#333' }}>No pending drafts</h5>
                    <p className="text-muted mb-3">
                      Click "Generate Follow-Ups" to have Jasper analyze your leads and draft personalized emails.
                    </p>
                    <Button
                      onClick={handleGenerateDrafts}
                      style={{ background: 'linear-gradient(135deg, #C9952A 0%, #E8B44A 100%)', border: 'none', fontWeight: 600 }}
                    >
                      <FontAwesomeIcon icon={faBolt} className="me-1" /> Generate Follow-Ups
                    </Button>
                  </Card.Body>
                </Card>
              )}

              {/* Upcoming Scheduled Automations */}
              {upcomingTasks.length > 0 && (
                <Card className="mb-4">
                  <Card.Header className="bg-white">
                    <h6 style={{ margin: 0, fontWeight: 700 }}>
                      <FontAwesomeIcon icon={faCalendarAlt} className="me-2" style={{ color: '#1976d2' }} />
                      Scheduled Automations ({upcomingTasks.length})
                    </h6>
                  </Card.Header>
                  <Card.Body style={{ padding: 0 }}>
                    <Table hover className="mb-0">
                      <thead>
                        <tr>
                          <th>Lead</th>
                          <th>Action</th>
                          <th>Priority</th>
                          <th>Scheduled</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {upcomingTasks.map((task: any) => {
                          const taskData = typeof task.task_data === 'string' ? JSON.parse(task.task_data) : task.task_data || {};
                          return (
                            <tr key={task.task_id}>
                              <td>
                                <div style={{ fontWeight: 600 }}>{task.lead_first_name} {task.lead_last_name}</div>
                                <small className="text-muted">{task.lead_email}</small>
                              </td>
                              <td>
                                <Badge bg="info" style={{ fontSize: '10px' }}>
                                  {(task.action || '').replace(/^coach_/, '').replace(/_/g, ' ')}
                                </Badge>
                                {taskData.reason && <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>{taskData.reason.substring(0, 80)}</div>}
                              </td>
                              <td>
                                <Badge bg={task.priority === 'urgent' ? 'danger' : task.priority === 'high' ? 'warning' : 'secondary'}>
                                  {task.priority}
                                </Badge>
                              </td>
                              <td><small>{new Date(task.scheduled_for) <= new Date() ? 'Ready now' : new Date(task.scheduled_for).toLocaleDateString()}</small></td>
                              <td>
                                <Button size="sm" style={{ fontSize: '10px', padding: '3px 10px', background: 'linear-gradient(135deg, #C9952A 0%, #E8B44A 100%)', border: 'none', fontWeight: 600 }}
                                  onClick={async () => {
                                    try {
                                      await axiosInstance.post(`/ai-coach/upcoming/${task.task_id}/approve`);
                                      toast.success(`Approved: ${task.action?.replace(/_/g, ' ')} for ${task.lead_first_name}`);
                                      fetchUpcomingTasks();
                                    } catch (err) { toast.error('Failed'); }
                                  }}
                                >Approve</Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}

              {/* Cold Leads */}
              {coldLeads.length > 0 && (
                <Card>
                  <Card.Header className="bg-white">
                    <h6 style={{ margin: 0, fontWeight: 700 }}>
                      <FontAwesomeIcon icon={faExclamationCircle || faBell} className="me-2 text-warning" />
                      Leads Needing Attention ({coldLeads.length})
                    </h6>
                  </Card.Header>
                  <Card.Body style={{ padding: 0 }}>
                    <Table hover className="mb-0">
                      <thead>
                        <tr>
                          <th>Lead</th>
                          <th>Type</th>
                          <th>Last Contact</th>
                          <th>Priority</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {coldLeads.slice(0, 10).map((lead: any) => (
                          <tr key={lead.leadId}>
                            <td>
                              <div style={{ fontWeight: 600 }}>{lead.leadName}</div>
                              <small className="text-muted">{lead.leadEmail}</small>
                            </td>
                            <td>
                              <Badge bg={lead.leadType === 'seller' ? 'warning' : 'info'}>{lead.leadType}</Badge>
                            </td>
                            <td>
                              <span style={{ color: lead.daysSinceContact >= 14 ? '#dc3545' : lead.daysSinceContact >= 7 ? '#fd7e14' : '#666' }}>
                                {lead.daysSinceContact} days ago
                              </span>
                            </td>
                            <td>
                              <Badge bg={lead.priority === 'high' ? 'danger' : 'warning'}>{lead.priority}</Badge>
                            </td>
                            <td>
                              <Button size="sm" variant="outline-primary" onClick={async () => {
                                try {
                                  setGeneratingDrafts(true);
                                  await axiosInstance.post('/ai-coach/draft-followup', { leadId: lead.leadId });
                                  toast.success(`Draft created for ${lead.leadName}`);
                                  await fetchCoachQueue();
                                } catch (err) {
                                  toast.error('Failed to generate draft');
                                }
                                setGeneratingDrafts(false);
                              }}>
                                Draft Follow-Up
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}
            </div>
          )}

          {/* Website Template Picker View */}
          {activeView === 'website' && (() => {
            const WEBSITE_TEMPLATES = [
              { id: 'classic',    name: 'Classic',    niche: 'Sellers',        colors: ['#1B3A4B', '#C8965A'], desc: 'Warm, seller-focused with serif elegance' },
              { id: 'modern',     name: 'Modern',     niche: 'Buyers',         colors: ['#0A0A0A', '#3B82F6'], desc: 'Dark, tech-forward with AI chat simulation' },
              { id: 'warm',       name: 'Warm',       niche: 'Relocation',     colors: ['#3D2B1F', '#E07A3A'], desc: 'Friendly, neighborhood-first for relocations' },
              { id: 'bold',       name: 'Bold',       niche: 'Investors',      colors: ['#0D0D0D', '#F5C842'], desc: 'High-energy investor-focused with data displays' },
              { id: 'minimal',    name: 'Minimal',    niche: 'Luxury',         colors: ['#FFFFFF', '#B8955A'], desc: 'Editorial luxury with understated elegance' },
              { id: 'team',       name: 'Team',       niche: 'Teams',          colors: ['#1E293B', '#10B981'], desc: 'Built for agent teams and group branding' },
              { id: 'video',      name: 'Video',      niche: 'Personal Brand', colors: ['#111111', '#FF6B6B'], desc: 'Cinematic personal-brand with video hero' },
              { id: 'community',  name: 'Community',  niche: 'Local Expert',   colors: ['#1B4332', '#F59E0B'], desc: 'Area expert with neighborhood focus' },
              { id: 'split',      name: 'Split',      niche: 'Modern',         colors: ['#312E81', '#7C3AED'], desc: 'Clean split-screen modern minimal design' },
              { id: 'starter',    name: 'Starter',    niche: 'New Agents',     colors: ['#0EA5E9', '#FBBF24'], desc: 'Friendly and approachable for new agents' },
              { id: 'dark-luxe',  name: 'Dark Luxe',  niche: 'Luxury',         colors: ['#0A0A0A', '#D4AF37'], desc: 'Dark mode elegance for high-end luxury' },
              { id: 'coastal',    name: 'Coastal',    niche: 'Coastal',        colors: ['#0D9488', '#D97706'], desc: 'Breezy lifestyle for resort and beach markets' },
            ];
            const currentTemplateId = portalSettings?.template_id || null;
            const portalSlug = portalSettings?.slug || '';
            const isDev = window.location.hostname.includes('dev.');
            const portalDomain = isDev ? 'https://dev.aisearchportal.com' : 'https://www.aisearchportal.com';
            const previewBase = `${portalDomain}/${portalSlug}`;

            return (
              <div>
                {/* Info banner */}
                <Card className="mb-4" style={{ borderLeft: '4px solid #C9952A', background: '#FFFDF5' }}>
                  <Card.Body>
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <h6 className="mb-1" style={{ color: '#0F1E35', fontWeight: 700 }}>
                          <FontAwesomeIcon icon={faGlobe} className="me-2" style={{ color: '#C9952A' }} />
                          Your Website
                        </h6>
                        <small className="text-muted">
                          Choose a template for your agent website at{' '}
                          <a href={previewBase} target="_blank" rel="noopener noreferrer" style={{ color: '#C9952A', fontWeight: 600 }}>
                            aisearchportal.com/{portalSlug}
                          </a>
                        </small>
                      </div>
                      {currentTemplateId && (
                        <span style={{ background: '#C9952A', color: '#0F1E35', padding: '4px 14px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Active: {WEBSITE_TEMPLATES.find(t => t.id === currentTemplateId)?.name || currentTemplateId}
                        </span>
                      )}
                    </div>
                  </Card.Body>
                </Card>

                {/* Template Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
                  {WEBSITE_TEMPLATES.map(template => {
                    const isActive = currentTemplateId === template.id;
                    return (
                      <Card
                        key={template.id}
                        style={{
                          border: isActive ? '2px solid #C9952A' : '1px solid #e0e0e0',
                          borderRadius: '12px',
                          overflow: 'hidden',
                          transition: 'all 0.2s',
                          boxShadow: isActive ? '0 4px 20px rgba(201,149,42,0.15)' : 'none',
                        }}
                      >
                        {/* Color preview */}
                        <div style={{
                          height: '120px',
                          background: `linear-gradient(135deg, ${template.colors[0]} 60%, ${template.colors[1]})`,
                          position: 'relative',
                        }}>
                          {isActive && (
                            <span style={{
                              position: 'absolute', top: '8px', right: '8px',
                              background: '#C9952A', color: '#0F1E35',
                              padding: '3px 10px', borderRadius: '12px',
                              fontSize: '10px', fontWeight: 700, textTransform: 'uppercase',
                            }}>
                              Active
                            </span>
                          )}
                          <span style={{
                            position: 'absolute', bottom: '8px', left: '8px',
                            background: 'rgba(0,0,0,0.3)', color: '#fff',
                            padding: '2px 8px', borderRadius: '8px',
                            fontSize: '10px', fontWeight: 600,
                          }}>
                            {template.niche}
                          </span>
                        </div>

                        <Card.Body>
                          <h6 style={{ color: '#0F1E35', fontWeight: 700, marginBottom: '4px' }}>{template.name}</h6>
                          <p style={{ fontSize: '12px', color: '#888', marginBottom: '16px', lineHeight: 1.4 }}>{template.desc}</p>

                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              style={{ flex: 1, fontSize: '12px', fontWeight: 600 }}
                              onClick={() => window.open(`${previewBase}?template=${template.id}`, '_blank')}
                            >
                              Preview
                            </Button>
                            {isActive ? (
                              <Button variant="secondary" size="sm" disabled style={{ flex: 1, fontSize: '12px', fontWeight: 600 }}>
                                Current
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                style={{ flex: 1, fontSize: '12px', fontWeight: 700, background: 'linear-gradient(135deg, #C9952A, #E8B44A)', border: 'none', color: '#0F1E35' }}
                                onClick={async () => {
                                  try {
                                    const res = await axiosInstance.put('/portal-settings/template', { templateId: template.id });
                                    if (res.data.success) {
                                      setPortalSettings((prev: any) => ({ ...prev, template_id: template.id }));
                                      alert(`Template updated to "${template.name}"! Your site has been updated.`);
                                    }
                                  } catch (err) {
                                    alert('Failed to update template. Please try again.');
                                  }
                                }}
                              >
                                Use This
                              </Button>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Settings View */}
          {activeView === 'settings' && (() => {
            if (savedCrmCredentials.length === 0) loadCrmCredentials();
            return (
            <div className="settings-view">
              {/* KOPS-539: MLS Profile Enrichment */}
              <Card className="mb-4" style={{ borderLeft: '4px solid #C9952A' }}>
                <Card.Body className="d-flex align-items-center justify-content-between">
                  <div>
                    <h6 className="mb-1" style={{ color: '#0F1E35', fontWeight: 700 }}>Auto-Fill from MLS</h6>
                    <small className="text-muted">
                      Populate your profile automatically from ARMLS — name, brokerage, license, photo, bio, and production history.
                    </small>
                  </div>
                  <Button
                    variant="warning"
                    style={{ background: 'linear-gradient(135deg, #C9952A, #E8B44A)', border: 'none', color: '#0F1E35', fontWeight: 700, whiteSpace: 'nowrap' }}
                    onClick={async () => {
                      try {
                        const btn = document.getElementById('mls-enrich-btn') as HTMLButtonElement;
                        if (btn) { btn.disabled = true; btn.textContent = 'Searching MLS...'; }
                        const res = await axiosInstance.post('/portal-settings/enrich-from-mls', {
                          licenseNumber: portalSettings?.license_number || null
                        });
                        if (res.data.success) {
                          // Reload settings to show updated fields
                          const settingsResponse = await agentPortalService.getPortalSettings();
                          setPortalSettings(settingsResponse.data);
                          toast.success(res.data.message || 'Profile updated from MLS!');
                        } else {
                          toast.error(res.data.message || 'No MLS data found for your email.');
                        }
                      } catch (err: any) {
                        const msg = err.response?.data?.message || 'No MLS match found.';
                        toast.error(msg + ' Tip: Enter your License Number below, save, then try again.');
                      } finally {
                        const btn = document.getElementById('mls-enrich-btn') as HTMLButtonElement;
                        if (btn) { btn.disabled = false; btn.textContent = 'Auto-Fill from MLS'; }
                      }
                    }}
                    id="mls-enrich-btn"
                  >
                    Auto-Fill from MLS
                  </Button>
                </Card.Body>
              </Card>

              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">Portal Configuration</h5>
                </Card.Header>
                <Card.Body>
                  <Form>
                    <Form.Group className="mb-3">
                      <Form.Label>Portal URL</Form.Label>
                      <div className="input-group">
                        <span className="input-group-text">aisearchportal.com/</span>
                        <Form.Control 
                          type="text" 
                          value={portalSettings?.slug || ''} 
                          onChange={async (e) => {
                            const newSlug = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                            setPortalSettings({...portalSettings, slug: newSlug});
                            
                            // Check slug availability
                            if (newSlug.length >= 3) {
                              setIsCheckingSlug(true);
                              try {
                                const result = await agentPortalService.checkSlugAvailability(newSlug);
                                setSlugError(!result.available ? result.message : '');
                              } catch (error) {
                                setSlugError('Unable to check availability');
                              }
                              setIsCheckingSlug(false);
                            } else if (newSlug.length > 0) {
                              setSlugError('URL must be at least 3 characters');
                            } else {
                              setSlugError('');
                            }
                          }}
                          placeholder="your-name"
                          isInvalid={!!slugError}
                        />
                        {isCheckingSlug && <span className="input-group-text"><Spinner size="sm" /></span>}
                      </div>
                      {slugError && <Form.Control.Feedback type="invalid">{slugError}</Form.Control.Feedback>}
                      <Form.Text className="text-muted">
                        Choose a unique URL for your portal (letters, numbers, and hyphens only)
                      </Form.Text>
                    </Form.Group>

                    {/* AI Portal Link & QR Code */}
                    {portalSettings?.slug && (
                      <div className="mb-3 p-3 rounded" style={{ background: '#f8f9fa', border: '1px solid #e9ecef' }}>
                        <Form.Label style={{ fontWeight: 600 }}>Your AI Portal Link & QR Code</Form.Label>
                        <p className="text-muted" style={{ fontSize: '13px', marginBottom: '8px' }}>
                          Share this link with your clients. They can search properties, save favorites, schedule showings, and get home valuations — all on your branded portal.
                        </p>
                        <div className="d-flex align-items-start gap-3">
                          <div style={{ flex: 1 }}>
                            <div className="input-group input-group-sm mb-2">
                              <input
                                type="text"
                                className="form-control"
                                readOnly
                                value={`https://aisearchportal.com/${portalSettings.slug}`}
                                style={{ fontSize: '12px' }}
                              />
                              <button
                                className="btn btn-outline-secondary"
                                type="button"
                                onClick={() => {
                                  navigator.clipboard.writeText(`https://aisearchportal.com/${portalSettings.slug}`);
                                  alert('Portal link copied!');
                                }}
                              >
                                Copy
                              </button>
                            </div>
                            <div style={{ fontSize: '11px', color: '#888' }}>
                              Add this to your email signature, business cards, and social media. Buyer activity is tracked in your Activity tab.
                            </div>
                          </div>
                          <div style={{ textAlign: 'center', minWidth: '120px' }}>
                            <img
                              src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(`https://aisearchportal.com/${portalSettings.slug}`)}`}
                              alt="QR Code"
                              style={{ width: '120px', height: '120px', border: '1px solid #ddd', borderRadius: '4px' }}
                            />
                            <div style={{ fontSize: '10px', color: '#888', marginTop: '4px' }}>Scan to visit portal</div>
                            <button
                              className="btn btn-outline-secondary btn-sm mt-1"
                              style={{ fontSize: '10px', padding: '2px 8px' }}
                              onClick={() => {
                                const link = document.createElement('a');
                                link.href = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(`https://aisearchportal.com/${portalSettings.slug}`)}`;
                                link.download = `qr-code-${portalSettings.slug}.png`;
                                link.click();
                              }}
                            >
                              Download QR
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    <Form.Group className="mb-3">
                      <Form.Label>Brokerage Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={portalSettings?.brokerage_name || ''}
                        onChange={(e) => setPortalSettings({...portalSettings, brokerage_name: e.target.value})}
                        placeholder="Your Brokerage"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Brokerage Logo</Form.Label>
                      {portalSettings?.logo_url && (
                        <div className="mb-2">
                          <img
                            src={portalSettings.logo_url}
                            alt="Brokerage Logo"
                            style={{ maxHeight: '60px', maxWidth: '200px', objectFit: 'contain', border: '1px solid #dee2e6', borderRadius: '4px', padding: '4px' }}
                          />
                        </div>
                      )}
                      <Form.Control
                        type="file"
                        accept="image/png,image/jpeg,image/svg+xml,image/webp"
                        onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 5 * 1024 * 1024) {
                            alert('Logo file must be under 5MB');
                            return;
                          }
                          try {
                            const formData = new FormData();
                            formData.append('logo', file);
                            const response = await axiosInstance.post('/portal-settings/logo', formData, {
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            if (response.data.success) {
                              setPortalSettings({...portalSettings, logo_url: response.data.logo_url});
                              alert('Logo uploaded successfully!');
                            }
                          } catch (err) {
                            console.error('Logo upload failed:', err);
                            alert('Failed to upload logo. Please try again.');
                          }
                        }}
                      />
                      <Form.Text className="text-muted">Upload your brokerage logo (PNG, JPG, SVG - max 5MB). Displayed on shared reports.</Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Agent Photo</Form.Label>
                      {portalSettings?.profile_photo_url && (
                        <div className="mb-2">
                          <img
                            src={portalSettings.profile_photo_url}
                            alt="Agent Photo"
                            className="rounded-circle"
                            style={{ width: '80px', height: '80px', objectFit: 'cover', border: '2px solid #dee2e6' }}
                          />
                        </div>
                      )}
                      <Form.Control
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 5 * 1024 * 1024) {
                            alert('Photo must be under 5MB');
                            return;
                          }
                          try {
                            const formData = new FormData();
                            formData.append('photo', file);
                            const response = await axiosInstance.post('/portal-settings/photo', formData, {
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            if (response.data.success) {
                              setPortalSettings({...portalSettings, profile_photo_url: response.data.photo_url});
                              alert('Photo uploaded successfully!');
                            }
                          } catch (err) {
                            console.error('Photo upload failed:', err);
                            alert('Failed to upload photo. Please try again.');
                          }
                        }}
                      />
                      <Form.Text className="text-muted">Upload your professional headshot (PNG, JPG - max 5MB). Shown on buyer and seller portals.</Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>License Number</Form.Label>
                      <Form.Control 
                        type="text" 
                        value={portalSettings?.license_number || ''}
                        onChange={(e) => setPortalSettings({...portalSettings, license_number: e.target.value})}
                        placeholder="DRE #123456"
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Display Name</Form.Label>
                      <Form.Control 
                        type="text" 
                        value={portalSettings?.display_name || ''}
                        onChange={(e) => setPortalSettings({...portalSettings, display_name: e.target.value})}
                        placeholder="Your display name on the portal"
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Phone</Form.Label>
                      <Form.Control 
                        type="tel" 
                        value={portalSettings?.phone || ''}
                        onChange={(e) => setPortalSettings({...portalSettings, phone: e.target.value})}
                        placeholder="(555) 123-4567"
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Email</Form.Label>
                      <Form.Control 
                        type="email" 
                        value={portalSettings?.email || ''}
                        onChange={(e) => setPortalSettings({...portalSettings, email: e.target.value})}
                        placeholder="agent@example.com"
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Bio</Form.Label>
                      <Form.Control 
                        as="textarea"
                        rows={3}
                        value={portalSettings?.bio || ''}
                        onChange={(e) => setPortalSettings({...portalSettings, bio: e.target.value})}
                        placeholder="Tell potential clients about yourself..."
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Years of Experience</Form.Label>
                      <Form.Control 
                        type="number" 
                        value={portalSettings?.years_experience || ''}
                        onChange={(e) => setPortalSettings({...portalSettings, years_experience: parseInt(e.target.value) || 0})}
                        placeholder="10"
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Brand Color</Form.Label>
                      <Form.Control 
                        type="color" 
                        value={portalSettings?.brand_color || '#1976d2'}
                        onChange={(e) => setPortalSettings({...portalSettings, brand_color: e.target.value})}
                        style={{width: '100px'}}
                      />
                      <Form.Text className="text-muted">Choose your portal's primary color</Form.Text>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Welcome Message</Form.Label>
                      <Form.Control 
                        as="textarea"
                        rows={2}
                        value={portalSettings?.welcome_message || ''}
                        onChange={(e) => setPortalSettings({...portalSettings, welcome_message: e.target.value})}
                        placeholder="Welcome to my AI-powered property search portal..."
                      />
                    </Form.Group>
                    
                    <Form.Check 
                      type="switch"
                      id="published-switch"
                      label="Publish my agent profile"
                      checked={portalSettings?.is_published || false}
                      onChange={(e) => setPortalSettings({...portalSettings, is_published: e.target.checked})}
                      className="mb-3"
                    />

                    <Button 
                      variant="primary"
                      onClick={async () => {
                        if (slugError) {
                          alert('Please fix the URL error before saving');
                          return;
                        }
                        
                        setIsSavingSettings(true);
                        try {
                          const response = await agentPortalService.updatePortalSettings({
                            slug: portalSettings.slug,
                            display_name: portalSettings.display_name,
                            brokerage_name: portalSettings.brokerage_name,
                            license_number: portalSettings.license_number,
                            phone: portalSettings.phone,
                            email: portalSettings.email,
                            bio: portalSettings.bio,
                            years_experience: portalSettings.years_experience,
                            brand_color: portalSettings.brand_color,
                            welcome_message: portalSettings.welcome_message,
                            is_published: portalSettings.is_published
                          });
                          alert('Settings saved successfully!');
                          // Update local state with response
                          if (response.data) {
                            setPortalSettings(response.data);
                          }
                        } catch (error) {
                          console.error('Error saving settings:', error);
                          alert('Failed to save settings. Please try again.');
                        }
                        setIsSavingSettings(false);
                      }}
                      disabled={isSavingSettings || !!slugError}
                    >
                      {isSavingSettings ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </Form>
                </Card.Body>
              </Card>

              {/* Email Integration Card */}
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">
                    <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                    Email Integration
                  </h5>
                </Card.Header>
                <Card.Body>
                  <p className="text-muted mb-3">
                    Connect your Gmail or Outlook to send emails directly from your account and automatically sync conversations with your clients.
                  </p>

                  {/* Connected Accounts */}
                  {connectedEmails.length > 0 && (
                    <div className="mb-4">
                      <h6 className="mb-3">Connected Accounts</h6>
                      {connectedEmails.map((email: any) => (
                        <div
                          key={email.id}
                          className="d-flex align-items-center justify-content-between p-3 rounded mb-2"
                          style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}
                        >
                          <div className="d-flex align-items-center">
                            <FontAwesomeIcon
                              icon={email.provider === 'gmail' || email.provider === 'google' ? faGoogle : faMicrosoft}
                              className="me-3"
                              style={{
                                fontSize: '1.4rem',
                                color: email.provider === 'gmail' || email.provider === 'google' ? '#EA4335' : '#00A4EF'
                              }}
                            />
                            <div>
                              <div style={{ fontWeight: 600 }}>{email.email}</div>
                              <small className="text-muted">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-success me-1" />
                                Connected
                                {email.last_email_sync && (
                                  <> &middot; Last synced {new Date(email.last_email_sync).toLocaleDateString()}</>
                                )}
                              </small>
                            </div>
                          </div>
                          <div className="d-flex gap-2 align-items-center">
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', cursor: 'pointer', padding: '4px 10px', borderRadius: '4px', background: localStorage.getItem('defaultFromAccount') === String(email.id) ? '#e8f5e9' : '#f8f9fa', border: `1px solid ${localStorage.getItem('defaultFromAccount') === String(email.id) ? '#27ae60' : '#ddd'}`, color: localStorage.getItem('defaultFromAccount') === String(email.id) ? '#27ae60' : '#666', fontWeight: 600 }}>
                              <input
                                type="checkbox"
                                checked={localStorage.getItem('defaultFromAccount') === String(email.id)}
                                onChange={() => {
                                  localStorage.setItem('defaultFromAccount', String(email.id));
                                  toast.success(`${email.email} set as default send-from account`);
                                  // Force re-render
                                  setConnectedEmails(prev => [...prev]);
                                }}
                                style={{ accentColor: '#27ae60', width: '14px', height: '14px' }}
                              />
                              Default
                            </label>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={async () => {
                                try {
                                  toast.loading('Syncing emails...', { id: 'resync' });
                                  const res = await axiosInstance.post(`/agents/resync-emails/${email.id}`);
                                  if (res.data?.success) {
                                    toast.success(res.data.message || 'Email sync started!', { id: 'resync' });
                                  } else {
                                    toast.error(res.data?.error || 'Sync failed', { id: 'resync' });
                                  }
                                } catch (err: any) {
                                  toast.error(err?.response?.data?.error || 'Sync failed', { id: 'resync' });
                                }
                              }}
                            >
                              ↻ Resync
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDisconnectEmail(email.id)}
                              disabled={disconnectingEmail === email.id}
                            >
                              {disconnectingEmail === email.id ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                'Disconnect'
                              )}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Connect Buttons */}
                  <h6 className="mb-3">
                    {connectedEmails.length > 0 ? 'Add Another Account' : 'Connect Your Email'}
                  </h6>
                  <Row>
                    <Col md={6} className="mb-2">
                      <Button
                        variant="outline-dark"
                        className="w-100 d-flex align-items-center justify-content-center py-3"
                        onClick={() => handleConnectEmail('google')}
                        disabled={loadingEmailConnect}
                        style={{ borderRadius: '8px' }}
                      >
                        <FontAwesomeIcon icon={faGoogle} className="me-2" style={{ color: '#EA4335', fontSize: '1.2rem' }} />
                        <span style={{ fontWeight: 600 }}>Connect Gmail</span>
                      </Button>
                    </Col>
                    <Col md={6} className="mb-2">
                      <Button
                        variant="outline-dark"
                        className="w-100 d-flex align-items-center justify-content-center py-3"
                        onClick={() => handleConnectEmail('microsoft')}
                        disabled={loadingEmailConnect}
                        style={{ borderRadius: '8px' }}
                      >
                        <FontAwesomeIcon icon={faMicrosoft} className="me-2" style={{ color: '#00A4EF', fontSize: '1.2rem' }} />
                        <span style={{ fontWeight: 600 }}>Connect Outlook</span>
                      </Button>
                    </Col>
                  </Row>

                  {/* KOPS-611: Connect Yahoo (app password) */}
                  <Row>
                    <Col md={12} className="mb-2">
                      <Button
                        variant="outline-dark"
                        className="w-100 d-flex align-items-center justify-content-center py-3"
                        onClick={openYahoo}
                        style={{ borderRadius: '8px' }}
                      >
                        <FontAwesomeIcon icon={faYahoo} className="me-2" style={{ color: '#6001D2', fontSize: '1.2rem' }} />
                        <span style={{ fontWeight: 600 }}>Connect Yahoo</span>
                      </Button>
                    </Col>
                  </Row>

                  <div className="mt-3 p-3 rounded" style={{ backgroundColor: '#f0f4f8', border: '1px solid #d6dce4' }}>
                    <small className="text-muted">
                      <FontAwesomeIcon icon={faShieldAlt} className="me-1" />
                      <strong>Privacy:</strong> We only access emails involving your CRM clients. Your full inbox is never read or stored.
                    </small>
                  </div>

                  {/* KOPS-611: Yahoo connect modal (app password) */}
                  <Modal show={showYahoo} onHide={() => setShowYahoo(false)} centered>
                    <Modal.Header closeButton style={{ borderBottom: '3px solid #6001D2' }}>
                      <Modal.Title style={{ color: '#6001D2', fontWeight: 'bold' }}>
                        <FontAwesomeIcon icon={faYahoo} className="me-2" />Connect Yahoo Mail
                      </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                      {yahooSuccess ? (
                        <Alert variant="success" className="mb-0">{yahooSuccess}</Alert>
                      ) : (
                        <>
                          <p className="mb-2">Yahoo needs a one-time <strong>app password</strong> (not your normal password) — secure and revocable anytime.</p>
                          <ol style={{ fontSize: '0.9rem' }}>
                            <li>Go to <a href="https://login.yahoo.com/myaccount/security" target="_blank" rel="noreferrer">Yahoo Account Security</a> (enable 2-step verification if needed)</li>
                            <li>Click <strong>Generate app password</strong>, name it <strong>KairosIQ</strong></li>
                            <li>Copy the 16-character password and paste it below</li>
                          </ol>
                          <Form.Group className="mb-3">
                            <Form.Label>Yahoo email</Form.Label>
                            <Form.Control type="email" placeholder="you@yahoo.com" value={yahooEmail} onChange={(e) => setYahooEmail(e.target.value)} disabled={yahooLoading} />
                          </Form.Group>
                          <Form.Group className="mb-3">
                            <Form.Label>App password (16 characters)</Form.Label>
                            <Form.Control type="password" placeholder="xxxx xxxx xxxx xxxx" value={yahooPassword} onChange={(e) => setYahooPassword(e.target.value)} disabled={yahooLoading} autoComplete="off" />
                            <Form.Text className="text-muted">Stored AES-256 encrypted — never your main password.</Form.Text>
                          </Form.Group>
                          <Form.Check type="checkbox" id="yahooConsentDash" label="I consent to KairosIQ reading and classifying my email to build my book (encrypted, private cloud)." checked={yahooConsent} onChange={(e) => setYahooConsent(e.target.checked)} className="mb-2" />
                          {yahooError && <Alert variant="danger" className="mt-2 mb-0 py-2">{yahooError}</Alert>}
                        </>
                      )}
                    </Modal.Body>
                    <Modal.Footer>
                      <Button variant="secondary" onClick={() => setShowYahoo(false)}>{yahooSuccess ? 'Close' : 'Cancel'}</Button>
                      {!yahooSuccess && (
                        <Button variant="primary" onClick={submitYahoo} disabled={yahooLoading || !yahooConsent}>
                          {yahooLoading ? <><Spinner size="sm" animation="border" className="me-2" />Connecting…</> : 'Connect Yahoo'}
                        </Button>
                      )}
                    </Modal.Footer>
                  </Modal>
                </Card.Body>
              </Card>

              {/* Change Password */}
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">
                    <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
                    Change Password
                  </h5>
                </Card.Header>
                <Card.Body>
                  <Row className="g-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>Current Password</Form.Label>
                        <Form.Control type="password" size="sm" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="Enter current password" />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>New Password</Form.Label>
                        <Form.Control type="password" size="sm" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min 8 characters" />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>Confirm New Password</Form.Label>
                        <Form.Control type="password" size="sm" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm new password" />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Button className="mt-3" size="sm" disabled={changingPassword || !currentPassword || !newPassword || newPassword !== confirmPassword || newPassword.length < 8}
                    style={{ background: 'linear-gradient(135deg, #C9952A 0%, #E8B44A 100%)', border: 'none', fontWeight: 600 }}
                    onClick={async () => {
                      setChangingPassword(true);
                      try {
                        await axiosInstance.post('/agents/change-password', {
                          currentPassword, newPassword, confirmPassword
                        });
                        toast.success('Password changed successfully!');
                        setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
                      } catch (err: any) {
                        toast.error(err.response?.data?.message || 'Failed to change password');
                      }
                      setChangingPassword(false);
                    }}
                  >
                    {changingPassword ? 'Changing...' : 'Update Password'}
                  </Button>
                  {newPassword && newPassword.length < 8 && <small className="text-danger d-block mt-1">Password must be at least 8 characters</small>}
                  {newPassword && confirmPassword && newPassword !== confirmPassword && <small className="text-danger d-block mt-1">Passwords do not match</small>}
                </Card.Body>
              </Card>

              <Card>
                <Card.Header>
                  <h5 className="mb-0">Subscription</h5>
                </Card.Header>
                <Card.Body>
                  <div className="subscription-info">
                    <Row className="align-items-center">
                      <Col>
                        <h6>Current Plan</h6>
                        <p className="mb-0">
                          {agentData?.subscriptionStatus === 'trialing' ? 'Free Trial' : 'Professional'}
                        </p>
                      </Col>
                      <Col xs="auto">
                        <Button variant="outline-primary">
                          {agentData?.subscriptionStatus === 'trialing' ? 'Upgrade to Pro' : 'Manage Billing'}
                        </Button>
                      </Col>
                    </Row>
                  </div>
                </Card.Body>
              </Card>

              {/* Property Intelligence Scan */}
              <Card className="mb-4" style={{ borderLeft: '4px solid #27ae60' }}>
                <Card.Body>
                  <h6 style={{ color: '#0F1E35', fontWeight: 700, marginBottom: '8px' }}>Property Intelligence Scan</h6>
                  <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                    Search county records to find what properties your clients own. Detects foreclosures, absentee owners, title changes, and property values.
                    Runs on past buyers, sellers, and sphere contacts.
                  </p>
                  <div className="d-flex gap-2 flex-wrap">
                    <Button
                      onClick={async () => {
                        const btn = document.getElementById('prop-scan-btn') as HTMLButtonElement;
                        if (btn) { btn.disabled = true; btn.textContent = 'Scanning...'; }
                        try {
                          const { data } = await axiosInstance.post('/ai-coach/scan-properties');
                          alert(`Property scan complete!\n\n${data.leadsScanned || 0} leads scanned\n${data.propertiesFound || 0} properties found\n${data.changesDetected || 0} changes detected\n\nCheck your clients' Property Ownership cards.`);
                        } catch {
                          alert('Property scan failed — try again or contact support');
                        }
                        if (btn) { btn.disabled = false; btn.textContent = 'Find Properties'; }
                      }}
                      id="prop-scan-btn"
                      style={{ background: '#0F1E35', border: 'none', fontWeight: 700 }}
                    >
                      Find Properties
                    </Button>
                    <Button
                      onClick={async () => {
                        const btn = document.getElementById('verify-btn') as HTMLButtonElement;
                        if (btn) { btn.disabled = true; btn.textContent = 'Verifying...'; }
                        try {
                          const { data } = await axiosInstance.post('/ai-coach/verify-ownership');
                          alert(`Ownership verification complete!\n\n${data.verified || 0} properties checked\n${data.stillOwns || 0} still owned by your clients\n${data.sold || 0} properties SOLD (no longer owned)\n\nCheck Activity tab for alerts.`);
                        } catch {
                          alert('Verification failed — try again or contact support');
                        }
                        if (btn) { btn.disabled = false; btn.textContent = 'Verify Ownership'; }
                      }}
                      id="verify-btn"
                      style={{ background: '#27ae60', border: 'none', fontWeight: 700 }}
                    >
                      Verify Ownership
                    </Button>
                  </div>
                  <div style={{ fontSize: '11px', color: '#888', marginTop: '8px' }}>
                    This runs automatically on the 1st of every month. Use this button for the initial scan or to refresh anytime.
                  </div>
                </Card.Body>
              </Card>

              {/* CRM Integrations */}
              <Card className="mb-4" style={{ borderLeft: '4px solid #3498db' }}>
                <Card.Body>
                  <h6 style={{ color: '#0F1E35', fontWeight: 700, marginBottom: '16px' }}>CRM Integrations</h6>
                  <p style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>
                    Import your leads from Follow Up Boss, kvCORE, or Wise Agent. Enter your API key below — we'll pull all your contacts, classify them with AI, and set up automated workflows.
                  </p>

                  {/* CRM Type Selector */}
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                    {[
                      { value: 'crm_followup_boss', label: 'Follow Up Boss' },
                      { value: 'crm_kv_core', label: 'kvCORE' },
                      { value: 'crm_cinc', label: 'CINC' },
                      { value: 'crm_boomtown', label: 'BoomTown' },
                      { value: 'crm_sierra', label: 'Sierra Interactive' },
                      { value: 'crm_lofty', label: 'Lofty (Chime)' },
                      { value: 'crm_wise_agent', label: 'Wise Agent' },
                      { value: 'crm_other', label: 'Other CRM' },
                    ].map(crm => (
                      <button
                        key={crm.value}
                        onClick={() => { setCrmType(crm.value as any); setCrmStatus(null); setCrmApiKey(''); }}
                        style={{ padding: '6px 14px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', cursor: 'pointer', border: crmType === crm.value ? '2px solid #0F1E35' : '1px solid #ddd', background: crmType === crm.value ? '#0F1E35' : 'white', color: crmType === crm.value ? 'white' : '#555' }}
                      >
                        {crm.label}
                      </button>
                    ))}
                  </div>

                  {/* Saved Credentials */}
                  {savedCrmCredentials.filter(c => c.service_type?.startsWith('crm_')).length > 0 && (
                    <div style={{ marginBottom: '14px', padding: '12px', background: '#f8f9fa', borderRadius: '6px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 600, color: '#555', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Connected CRMs</div>
                      {savedCrmCredentials.filter(c => c.service_type?.startsWith('crm_')).map((cred: any) => (
                        <div key={cred.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #eee' }}>
                          <div>
                            <span style={{ fontSize: '13px', fontWeight: 600, color: '#0F1E35' }}>{cred.service_name || cred.service_type}</span>
                            <span style={{ fontSize: '11px', color: cred.is_validated ? '#27ae60' : '#e67e22', marginLeft: '8px' }}>
                              {cred.is_validated ? '● Connected' : '● Saved (not validated)'}
                            </span>
                          </div>
                          <span style={{ fontSize: '11px', color: '#999' }}>
                            Key: ••••••{cred.api_key_last4 || '••••'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* API Key Input */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ fontSize: '12px', fontWeight: 600, color: '#555', display: 'block', marginBottom: '4px' }}>
                      {crmType === 'crm_followup_boss' ? 'Follow Up Boss API Key' : crmType === 'crm_wise_agent' ? 'Wise Agent API Key / Token' : 'kvCORE API Key (Bearer Token)'}
                    </label>
                    <div className="d-flex gap-2">
                      <input
                        type="password"
                        value={crmApiKey}
                        onChange={e => { setCrmApiKey(e.target.value); setCrmStatus(null); }}
                        placeholder={crmType === 'crm_followup_boss' ? 'Enter your FUB API key...' : crmType === 'crm_wise_agent' ? 'Enter your Wise Agent API key or OAuth token...' : 'Enter your kvCORE Bearer token...'}
                        style={{ flex: 1, padding: '8px 12px', fontSize: '13px', borderRadius: '6px', border: '1px solid #ddd', outline: 'none' }}
                      />
                      <Button
                        disabled={!crmApiKey.trim() || crmValidating}
                        onClick={async () => {
                          setCrmValidating(true);
                          setCrmStatus(null);
                          try {
                            const { data } = await axiosInstance.post('/api-credentials', {
                              service_type: crmType,
                              service_name: crmType === 'crm_followup_boss' ? 'Follow Up Boss' : crmType === 'crm_wise_agent' ? 'Wise Agent' : crmType === 'crm_kv_core' ? 'KV Core' : crmType.replace('crm_', '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                              api_key: crmApiKey,
                              credential_format: 'api_key'
                            });
                            if (data.success) {
                              setCrmStatus({ type: 'success', message: 'API key saved and validated! Ready to import.' });
                            } else {
                              setCrmStatus({ type: 'error', message: data.message || 'Validation failed' });
                            }
                          } catch (err: any) {
                            setCrmStatus({ type: 'error', message: err.response?.data?.message || 'Failed to save credentials' });
                          }
                          setCrmValidating(false);
                        }}
                        style={{ background: '#0F1E35', border: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                      >
                        {crmValidating ? 'Validating...' : 'Save & Validate'}
                      </Button>
                    </div>
                  </div>

                  {/* Where to find your API key */}
                  <div style={{ fontSize: '11px', color: '#888', marginBottom: '14px', lineHeight: '1.6' }}>
                    {crmType === 'crm_followup_boss' && <span><strong>Where to find it:</strong> Follow Up Boss → Admin → API → copy your API key</span>}
                    {crmType === 'crm_kv_core' && <span><strong>Where to find it:</strong> kvCORE → Settings → API / Integrations → generate API token</span>}
                    {crmType === 'crm_cinc' && <span><strong>Where to find it:</strong> CINC → Settings → API Access → copy your API key</span>}
                    {crmType === 'crm_boomtown' && <span><strong>Where to find it:</strong> BoomTown → Admin → Integrations → API credentials</span>}
                    {crmType === 'crm_sierra' && <span><strong>Where to find it:</strong> Sierra Interactive → Settings → API → generate token</span>}
                    {crmType === 'crm_lofty' && <span><strong>Where to find it:</strong> Lofty (Chime) → Settings → Developer API → create API key</span>}
                    {crmType === 'crm_wise_agent' && <span><strong>Where to find it:</strong> Wise Agent → Settings → API / Integrations → copy your API key or OAuth token. We'll try both auth methods automatically.</span>}
                    {crmType === 'crm_other' && <span><strong>Contact us:</strong> Text Jasper at (520) 251-9595 or email support@kairosiq.ai with your CRM name and we'll build the integration.</span>}
                  </div>

                  {/* Coming soon for unsupported CRMs */}
                  {!['crm_followup_boss', 'crm_kv_core', 'crm_wise_agent'].includes(crmType) && crmType !== 'crm_other' && (
                    <div style={{ padding: '12px 16px', borderRadius: '6px', background: '#eff6ff', border: '1px solid #bfdbfe', fontSize: '13px', color: '#1e40af', marginBottom: '12px' }}>
                      <strong>{crmType.replace('crm_', '').replace(/_/g, ' ').toUpperCase()}</strong> integration is coming soon. Save your API key now — we'll notify you when it's ready, and your leads will import automatically.
                    </div>
                  )}

                  {/* Status */}
                  {crmStatus && (
                    <div style={{ padding: '10px 14px', borderRadius: '6px', fontSize: '13px', marginBottom: '12px', background: crmStatus.type === 'success' ? '#f0fdf4' : crmStatus.type === 'importing' ? '#eff6ff' : '#fef2f2', color: crmStatus.type === 'success' ? '#166534' : crmStatus.type === 'importing' ? '#1e40af' : '#991b1b', border: `1px solid ${crmStatus.type === 'success' ? '#bbf7d0' : crmStatus.type === 'importing' ? '#bfdbfe' : '#fecaca'}` }}>
                      {crmStatus.message}
                    </div>
                  )}

                  {/* Import Button */}
                  {crmStatus?.type === 'success' && (
                    <div className="d-flex gap-2 flex-wrap">
                      <Button
                        disabled={crmImporting}
                        onClick={async () => {
                          setCrmImporting(true);
                          setCrmStatus({ type: 'importing', message: 'Importing leads... This may take a few minutes for large databases.' });
                          try {
                            const { data } = await axiosInstance.post('/api-credentials/import-crm', { service_type: crmType });
                            if (data.success) {
                              setCrmStatus({ type: 'success', message: `Import complete! ${data.stats?.created || 0} leads imported, ${data.stats?.updated || 0} updated, ${data.stats?.contacts || 0} contact methods, ${data.stats?.classified || 0} classified. Click "Run Property Scan" to find properties your clients own.` });
                              if (classLeads.length > 0) loadClassifications();
                            } else {
                              setCrmStatus({ type: 'error', message: data.message || 'Import failed' });
                            }
                          } catch (err: any) {
                            setCrmStatus({ type: 'error', message: err.response?.data?.message || 'Import failed' });
                          }
                          setCrmImporting(false);
                        }}
                        style={{ background: 'linear-gradient(135deg, #C9952A, #E8B44A)', border: 'none', color: '#0F1E35', fontWeight: 700, padding: '10px 24px' }}
                      >
                        {crmImporting ? <><Spinner size="sm" className="me-2" />Importing...</> : 'Import All Leads'}
                      </Button>
                      <Button
                        disabled={crmImporting}
                        onClick={async () => {
                          setCrmStatus({ type: 'importing', message: 'Scanning county records for property ownership... This runs in the background (20-30 min for large databases). You can navigate away.' });
                          try {
                            const { data } = await axiosInstance.post('/ai-coach/scan-properties');
                            setCrmStatus({ type: 'success', message: `Property scan complete! ${data.propertiesFound || 0} properties found for ${data.leadsScanned || 0} leads. Check your clients\' Property Ownership cards.` });
                          } catch (err: any) {
                            setCrmStatus({ type: 'error', message: 'Property scan failed — try again or contact support' });
                          }
                        }}
                        style={{ background: '#0F1E35', border: 'none', color: 'white', fontWeight: 700, padding: '10px 24px' }}
                      >
                        Run Property Scan
                      </Button>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </div>
          );})()}

          {/* Help & Support */}
          {activeView === 'classifications' && (() => {
            if (!classLoading && classLeads.length === 0 && classTotalCount === -1) { setClassTotalCount(0); loadClassifications(1, 'all', ''); }
            const filtered = classLeads;

            return (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 style={{ fontFamily: "'Bebas Neue', sans-serif", color: '#0F1E35', letterSpacing: '1px', margin: 0 }}>
                    Lead <span style={{ color: '#C9952A' }}>Classifications</span>
                  </h4>
                  <Button size="sm" style={{ background: '#C9952A', border: 'none', fontWeight: 600 }} onClick={() => loadClassifications()}>
                    {classLoading ? <Spinner size="sm" /> : 'Refresh'}
                  </Button>
                </div>

                {/* Stage filter pills */}
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
                  <button
                    onClick={() => { setClassFilter('all'); loadClassifications(1, 'all', classSearch); }}
                    style={{ padding: '4px 12px', fontSize: '11px', fontWeight: 600, borderRadius: '14px', border: classFilter === 'all' ? '2px solid #0F1E35' : '1px solid #ddd', background: classFilter === 'all' ? '#0F1E35' : 'white', color: classFilter === 'all' ? 'white' : '#555', cursor: 'pointer' }}
                  >
                    All ({classTotalCount})
                  </button>
                  {classStages.filter(s => classStageCounts[s.value]).map(s => (
                    <button
                      key={s.value}
                      onClick={() => { const next = classFilter === s.value ? 'all' : s.value; setClassFilter(next); loadClassifications(1, next, classSearch); }}
                      style={{ padding: '4px 12px', fontSize: '11px', fontWeight: 600, borderRadius: '14px', border: classFilter === s.value ? `2px solid ${s.color}` : '1px solid #ddd', background: classFilter === s.value ? s.color : 'white', color: classFilter === s.value ? 'white' : '#555', cursor: 'pointer' }}
                    >
                      {s.label} ({classStageCounts[s.value]})
                    </button>
                  ))}
                </div>

                {/* Search */}
                <input
                  type="text" value={classSearch}
                  onChange={e => setClassSearch(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') loadClassifications(1, classFilter, classSearch); }}
                  placeholder="Search by name or email... (press Enter)"
                  style={{ width: '100%', padding: '8px 12px', fontSize: '13px', borderRadius: '6px', border: '1px solid #ddd', marginBottom: '12px', outline: 'none' }}
                />

                {/* Table */}
                {classLoading ? (
                  <div className="text-center py-4"><Spinner animation="border" /><div className="mt-2 text-muted">Loading classifications...</div></div>
                ) : (
                  <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #e9ecef', overflow: 'hidden' }}>
                    <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e9ecef' }}>
                          <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: '#0F1E35' }}>Name</th>
                          <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: '#0F1E35' }}>Email</th>
                          <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: '#0F1E35' }}>Classification</th>
                          <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: '#0F1E35' }}>Relationship Notes</th>
                          <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: '#0F1E35' }}>Activity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.slice(0, 100).map((lead: any) => {
                          const stageInfo = classStages.find(s => s.value === lead.consumer_journey_stage) || { label: lead.consumer_journey_stage || 'Unknown', color: '#ccc' };
                          const isEditing = editingNote === lead.id;
                          return (
                            <tr key={lead.id} style={{ borderBottom: '1px solid #f0f0f0' }}
                              onMouseEnter={e => (e.currentTarget.style.background = '#fafbfc')}
                              onMouseLeave={e => (e.currentTarget.style.background = 'white')}
                            >
                              <td style={{ padding: '8px 12px' }}>
                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                  {editingField?.id === lead.id && editingField?.field === 'firstName' ? (
                                    <input value={editFieldValue} onChange={e => setEditFieldValue(e.target.value)} autoFocus
                                      onKeyDown={e => { if (e.key === 'Enter') updateLeadField(lead.id, 'firstName', editFieldValue); if (e.key === 'Escape') setEditingField(null); }}
                                      onBlur={() => updateLeadField(lead.id, 'firstName', editFieldValue)}
                                      style={{ width: '80px', padding: '2px 4px', fontSize: '12px', fontWeight: 600, border: '1px solid #C9952A', borderRadius: '3px', outline: 'none' }} />
                                  ) : (
                                    <span style={{ fontWeight: 600, color: '#333', cursor: 'pointer' }} title="Click to edit"
                                      onClick={() => { setEditingField({ id: lead.id, field: 'firstName' }); setEditFieldValue(lead.lead_first_name || ''); }}>
                                      {lead.lead_first_name}
                                    </span>
                                  )}
                                  {editingField?.id === lead.id && editingField?.field === 'lastName' ? (
                                    <input value={editFieldValue} onChange={e => setEditFieldValue(e.target.value)} autoFocus
                                      onKeyDown={e => { if (e.key === 'Enter') updateLeadField(lead.id, 'lastName', editFieldValue); if (e.key === 'Escape') setEditingField(null); }}
                                      onBlur={() => updateLeadField(lead.id, 'lastName', editFieldValue)}
                                      style={{ width: '90px', padding: '2px 4px', fontSize: '12px', fontWeight: 600, border: '1px solid #C9952A', borderRadius: '3px', outline: 'none' }} />
                                  ) : (
                                    <span style={{ fontWeight: 600, color: '#333', cursor: 'pointer' }} title="Click to edit"
                                      onClick={() => { setEditingField({ id: lead.id, field: 'lastName' }); setEditFieldValue(lead.lead_last_name || ''); }}>
                                      {lead.lead_last_name || '—'}
                                    </span>
                                  )}
                                </div>
                                {editingField?.id === lead.id && editingField?.field === 'phone' ? (
                                  <input value={editFieldValue} onChange={e => setEditFieldValue(e.target.value)} autoFocus
                                    onKeyDown={e => { if (e.key === 'Enter') updateLeadField(lead.id, 'phone', editFieldValue); if (e.key === 'Escape') setEditingField(null); }}
                                    onBlur={() => updateLeadField(lead.id, 'phone', editFieldValue)}
                                    style={{ width: '120px', padding: '2px 4px', fontSize: '11px', border: '1px solid #C9952A', borderRadius: '3px', outline: 'none' }} />
                                ) : (
                                  <div style={{ fontSize: '11px', color: lead.lead_phone ? '#999' : '#ccc', cursor: 'pointer' }} title="Click to edit"
                                    onClick={() => { setEditingField({ id: lead.id, field: 'phone' }); setEditFieldValue(lead.lead_phone || ''); }}>
                                    {lead.lead_phone || 'Add phone...'}
                                  </div>
                                )}
                              </td>
                              <td style={{ padding: '8px 12px' }}>
                                {editingField?.id === lead.id && editingField?.field === 'email' ? (
                                  <input value={editFieldValue} onChange={e => setEditFieldValue(e.target.value)} autoFocus
                                    onKeyDown={e => { if (e.key === 'Enter') updateLeadField(lead.id, 'email', editFieldValue); if (e.key === 'Escape') setEditingField(null); }}
                                    onBlur={() => updateLeadField(lead.id, 'email', editFieldValue)}
                                    style={{ width: '100%', padding: '2px 4px', fontSize: '12px', border: '1px solid #C9952A', borderRadius: '3px', outline: 'none' }} />
                                ) : (
                                  <span style={{ fontSize: '12px', color: '#666', cursor: 'pointer' }} title="Click to edit"
                                    onClick={() => { setEditingField({ id: lead.id, field: 'email' }); setEditFieldValue(lead.lead_email || ''); }}>
                                    {lead.lead_email || '—'}
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: '8px 12px' }}>
                                <select
                                  value={lead.consumer_journey_stage || 'unknown'}
                                  onChange={e => updateClassification(lead.id, e.target.value)}
                                  style={{ padding: '4px 8px', fontSize: '11px', fontWeight: 600, borderRadius: '4px', border: `1px solid ${stageInfo.color}`, color: stageInfo.color, background: 'white', cursor: 'pointer', outline: 'none' }}
                                >
                                  {classStages.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                </select>
                              </td>
                              <td style={{ padding: '8px 12px', maxWidth: '250px' }}>
                                {isEditing ? (
                                  <div className="d-flex gap-1">
                                    <input
                                      type="text" value={noteText2} onChange={e => setNoteText2(e.target.value)}
                                      onKeyDown={e => { if (e.key === 'Enter') { updateClassification(lead.id, undefined, noteText2); setEditingNote(null); } if (e.key === 'Escape') setEditingNote(null); }}
                                      autoFocus
                                      style={{ flex: 1, padding: '3px 6px', fontSize: '11px', border: '1px solid #C9952A', borderRadius: '4px', outline: 'none' }}
                                    />
                                    <button onClick={() => { updateClassification(lead.id, undefined, noteText2); setEditingNote(null); }}
                                      style={{ padding: '2px 8px', fontSize: '10px', background: '#C9952A', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Save</button>
                                  </div>
                                ) : (
                                  <div
                                    onClick={() => { setEditingNote(lead.id); setNoteText2(lead.relationship_notes || ''); }}
                                    style={{ fontSize: '11px', color: lead.relationship_notes ? '#444' : '#bbb', cursor: 'pointer', minHeight: '20px' }}
                                    title="Click to add/edit notes"
                                  >
                                    {lead.relationship_notes || 'Click to add notes...'}
                                  </div>
                                )}
                              </td>
                              <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                <div style={{ fontSize: '12px', fontWeight: 600, color: '#0F1E35' }}>{lead.activity_count || 0}</div>
                                <div style={{ fontSize: '10px', color: '#999' }}>
                                  {lead.last_activity ? new Date(lead.last_activity).toLocaleDateString() : '—'}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {classHasMore && (
                      <div style={{ padding: '12px', textAlign: 'center' }}>
                        <button
                          onClick={() => loadClassifications(classPage + 1, classFilter, classSearch)}
                          disabled={classLoading}
                          style={{ padding: '8px 24px', fontSize: '13px', fontWeight: 600, borderRadius: '6px', border: '1px solid #C9952A', background: 'white', color: '#C9952A', cursor: 'pointer' }}
                        >
                          {classLoading ? 'Loading...' : `Load More (showing ${filtered.length} of ${classTotalCount})`}
                        </button>
                      </div>
                    )}
                    {!classHasMore && filtered.length > 0 && <div style={{ padding: '10px', textAlign: 'center', fontSize: '12px', color: '#888' }}>Showing all {filtered.length} leads</div>}
                    {filtered.length === 0 && !classLoading && <div style={{ padding: '20px', textAlign: 'center', fontSize: '13px', color: '#888' }}>No leads match your filter</div>}
                  </div>
                )}
              </div>
            );
          })()}

          {activeView === 'communications' && (
            <CommunicationsTab />
          )}

          {activeView === 'chat' && (
            <InterofficeChatTab />
          )}

          {activeView === 'reports' && (
            <OnboardingReportsTab />
          )}

          {activeView === 'help' && (
            <div style={{ maxWidth: '800px' }}>
              <h4 style={{ fontFamily: "'Bebas Neue', sans-serif", color: '#0F1E35', letterSpacing: '1px', marginBottom: '24px' }}>
                Help & <span style={{ color: '#C9952A' }}>Support</span>
              </h4>

              {/* Contact Support */}
              <Card className="mb-3" style={{ borderLeft: '4px solid #3498db', background: 'linear-gradient(135deg, #0F1E35, #162844)' }}>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 style={{ fontWeight: 700, color: '#fff', marginBottom: '4px' }}>
                        <FontAwesomeIcon icon={faEnvelope} className="me-2" style={{ color: '#E8B44A' }} />
                        Need Help? Contact Support
                      </h6>
                      <p style={{ fontSize: '13px', color: '#8FA3BF', margin: 0 }}>
                        Send us a message and we'll get back to you quickly.
                      </p>
                    </div>
                    <Button
                      size="sm"
                      style={{ background: 'linear-gradient(135deg, #C9952A, #E8B44A)', border: 'none', color: '#0F1E35', fontWeight: 700, whiteSpace: 'nowrap', padding: '8px 20px' }}
                      onClick={async () => {
                        try {
                          const userId = agentData?.id || agentData?.user_id;
                          if (!userId) { toast.error('Please log in again'); return; }
                          const res = await axiosInstance.get(`/chat/support/conversations/${userId}`);
                          if (res.data.success) {
                            setSupportConvId(res.data.conversation.id);
                            setSupportMessages(res.data.messages || []);
                            setShowSupportChat(true);
                          }
                        } catch { toast.error('Failed to open support chat'); }
                      }}
                    >
                      <FontAwesomeIcon icon={faPaperPlane} className="me-1" /> Contact Support
                    </Button>
                  </div>
                </Card.Body>
              </Card>

              {/* Getting Started */}
              <Card className="mb-3" style={{ borderLeft: '4px solid #C9952A' }}>
                <Card.Body>
                  <h6 style={{ fontWeight: 700, color: '#0F1E35', marginBottom: '12px' }}>Getting Started (5 minutes)</h6>
                  <div style={{ fontSize: '14px', lineHeight: '2' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                      <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: connectedEmails?.length > 0 ? '#27ae60' : '#e9ecef', color: 'white', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700 }}>1</span>
                      <span><strong>Connect your email</strong> — {connectedEmails?.length > 0 ? <span style={{ color: '#27ae60' }}>Connected ✓</span> : <span style={{ color: '#e67e22' }}>Not connected — <a href="/agent-portal/settings">Go to Settings</a></span>}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                      <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#e9ecef', color: '#555', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700 }}>2</span>
                      <span><strong>Enter your MLS ID</strong> (optional) — Unlocks price predictions & comparable sales</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                      <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#e9ecef', color: '#555', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700 }}>3</span>
                      <span><strong>Review past clients</strong> — Confirm which contacts are real clients</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                      <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#e9ecef', color: '#555', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700 }}>4</span>
                      <span><strong>Import from your CRM</strong> (optional) — Pull leads from Follow Up Boss, kvCORE, or other CRM</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                      <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#e9ecef', color: '#555', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700 }}>5</span>
                      <span><strong>Run Property Scan</strong> — Find what properties your clients own via county records</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#e9ecef', color: '#555', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700 }}>6</span>
                      <span><strong>Enable monthly updates</strong> — Toggle on properties you want to nurture</span>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {/* How It Works */}
              <Card className="mb-3">
                <Card.Body>
                  <h6 style={{ fontWeight: 700, color: '#0F1E35', marginBottom: '12px' }}>How Your AI Coach Works</h6>
                  <div style={{ fontSize: '13px', lineHeight: '1.8', color: '#444' }}>
                    <p><strong>Daily Email Brief (7am)</strong> — Every morning you receive a prioritized action list: who to call, property alerts, leads going cold, and past clients to nurture.</p>
                    <p><strong>Approval Queue</strong> — AI writes personalized emails for you. Review, edit if needed, click Send. Nothing goes out without your approval.</p>
                    <p><strong>Property Intelligence</strong> — We monitor county records for all your past clients. When something changes (foreclosure, move, title transfer), you get an alert.</p>
                    <p><strong>Offer Analyzer</strong> — For buyer clients, get AI-predicted value + comps. Chat with AI about strategy, then draft an offer email backed by data.</p>
                    <p><strong>Monthly Home Value Updates</strong> — Enable per property. On the 1st of every month, your client gets a branded report showing their home's current value and neighborhood activity.</p>
                  </div>
                </Card.Body>
              </Card>

              {/* Your Daily Routine */}
              <Card className="mb-3" style={{ borderLeft: '4px solid #27ae60' }}>
                <Card.Body>
                  <h6 style={{ fontWeight: 700, color: '#0F1E35', marginBottom: '12px' }}>Your Daily Routine (2 minutes)</h6>
                  <div style={{ fontSize: '13px', lineHeight: '2', color: '#444' }}>
                    <div>1. Check your 7am email → see your game plan</div>
                    <div>2. Open Dashboard → review pending AI drafts</div>
                    <div>3. Send the good ones, dismiss the rest</div>
                    <div>4. When working with a buyer → use Offer Analyzer</div>
                  </div>
                  <div style={{ marginTop: '12px', padding: '10px 14px', background: '#f0fdf4', borderRadius: '6px', fontSize: '12px', color: '#166534' }}>
                    The AI handles research, writing, and monitoring. You focus on appointments and closings.
                  </div>
                </Card.Body>
              </Card>

              {/* The ROI */}
              <Card className="mb-3">
                <Card.Body>
                  <h6 style={{ fontWeight: 700, color: '#0F1E35', marginBottom: '12px' }}>Why This Pays for Itself</h6>
                  <div style={{ fontSize: '13px', color: '#444', lineHeight: '1.8' }}>
                    <p>65% of past buyers use a DIFFERENT agent when they sell. That's $87,270/year in lost commissions for the average Phoenix agent.</p>
                    <p style={{ fontWeight: 600 }}>Your AI Pro subscription: $185/month ($2,220/year)</p>
                    <p style={{ fontWeight: 600, color: '#C9952A' }}>Retain just ONE extra listing: $17,454 → 7.9x ROI</p>
                  </div>
                </Card.Body>
              </Card>

              {/* MLS Connectivity */}
              <Card className="mb-3" style={{ borderLeft: '4px solid #3498db' }}>
                <Card.Body>
                  <h6 style={{ fontWeight: 700, color: '#0F1E35', marginBottom: '8px' }}>
                    Unlock MLS Intelligence (Optional)
                  </h6>
                  <div style={{ fontSize: '13px', color: '#444', lineHeight: '1.8', marginBottom: '14px' }}>
                    <p style={{ marginBottom: '8px' }}>
                      Connecting your brokerage's MLS data feed unlocks <strong>AI price predictions, comparable sales analysis, and the Offer Analyzer</strong>.
                      Without MLS, the platform still works — AI classification, daily briefs, property ownership, and automations all function using your email and county records.
                    </p>
                    <p style={{ marginBottom: '8px', fontWeight: 600 }}>With MLS data you get:</p>
                    <ul style={{ margin: '0 0 12px', paddingLeft: '20px' }}>
                      <li>AI-powered property valuations using machine learning</li>
                      <li>Full comparable sales with address, sold price, beds/baths, DOM</li>
                      <li>Offer Analyzer with data-backed offer ranges</li>
                      <li>Monthly CMA reports with comp details for past clients</li>
                    </ul>
                  </div>

                  <div style={{ background: '#f0f7ff', borderRadius: '8px', padding: '14px 16px', marginBottom: '14px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#0F1E35', marginBottom: '8px' }}>How to Request MLS Access (ARMLS)</div>
                    <div style={{ fontSize: '12px', color: '#444', lineHeight: '1.8' }}>
                      <div style={{ marginBottom: '6px' }}>Your brokerage must approve and submit a Content License Agreement (CLA) through the ARMLS Collaboration Portal. Here's how:</div>
                      <ol style={{ margin: '0 0 10px', paddingLeft: '20px' }}>
                        <li>Go to <strong>armls.cobblestonesystems.com</strong> and log in with your ARMLS credentials</li>
                        <li>Click <strong>"Submit Request"</strong> at the top of the page</li>
                        <li>Select <strong>"Data License Request"</strong> from the dropdown</li>
                        <li>Select your role as <strong>"ARMLS Subscriber"</strong></li>
                        <li>For <strong>Permitted Use</strong>, request:
                          <div style={{ background: 'white', borderRadius: '4px', padding: '8px 12px', margin: '6px 0', border: '1px solid #ddd', fontSize: '11px' }}>
                            <div><strong>IDX Display</strong> — Powers property search and listing display on your portal</div>
                            <div><strong>Back Office / Internal Use</strong> — Powers CMA reports, market analysis, and AI valuation tools</div>
                          </div>
                        </li>
                        <li>For Consultant fields, enter KairosIQ's information:
                          <div style={{ background: 'white', borderRadius: '4px', padding: '8px 12px', margin: '6px 0', border: '1px solid #ddd', fontSize: '11px' }}>
                            <div><strong>Company:</strong> KairosIQ AI, Inc.</div>
                            <div><strong>Contact:</strong> Michael Boykin</div>
                            <div><strong>Email:</strong> support@kairosiq.ai</div>
                            <div><strong>Phone:</strong> (520) 251-9595</div>
                            <div><strong>Product:</strong> AI-powered CMA and market analysis platform for real estate agents</div>
                          </div>
                        </li>
                        <li>Submit the request — ARMLS will review and generate the agreement</li>
                      </ol>
                    </div>
                    <div style={{ fontSize: '11px', color: '#666', marginBottom: '10px' }}>
                      <strong>Important:</strong> Each brokerage gets 5 free data feeds (excluding VOW). After the 5th feed, a $150/month fee applies per additional feed. Broker approval is required — check with your broker before submitting.
                    </div>
                    <a
                      href="/docs/ARMLS-Instructions-Requesting-Contract.pdf"
                      download="ARMLS Instructions - Requesting a Contract.pdf"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', background: '#0F1E35', color: 'white', textDecoration: 'none', cursor: 'pointer' }}
                    >
                      <FontAwesomeIcon icon={faDownload} /> Download ARMLS Instructions (PDF)
                    </a>
                  </div>

                  <div style={{ fontSize: '11px', color: '#888', lineHeight: '1.6' }}>
                    Need help with MLS setup? Text Jasper at (520) 251-9595 or email support@kairosiq.ai — we'll walk you through it.
                  </div>
                </Card.Body>
              </Card>

              {/* CRM Import */}
              <Card className="mb-3" style={{ borderLeft: '4px solid #8e24aa' }}>
                <Card.Body>
                  <h6 style={{ fontWeight: 700, color: '#0F1E35', marginBottom: '12px' }}>Import Leads from Your CRM</h6>
                  <div style={{ fontSize: '13px', color: '#444', lineHeight: '1.8' }}>
                    <p style={{ marginBottom: '10px' }}>
                      Already have leads in another CRM? Import them into KairosIQ in minutes. We currently support:
                    </p>

                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '14px' }}>
                      <div style={{ flex: '1', minWidth: '200px' }}>
                        <div style={{ fontWeight: 600, color: '#0F1E35', fontSize: '12px', marginBottom: '6px' }}>Supported (Auto-Import)</div>
                        <ul style={{ fontSize: '12px', color: '#666', paddingLeft: '18px', margin: 0, lineHeight: '2' }}>
                          <li><strong>Follow Up Boss</strong> — full contact, deal, and activity import</li>
                          <li><strong>kvCORE</strong> — contacts, transactions, office data</li>
                        </ul>
                      </div>
                      <div style={{ flex: '1', minWidth: '200px' }}>
                        <div style={{ fontWeight: 600, color: '#0F1E35', fontSize: '12px', marginBottom: '6px' }}>Coming Soon (Save Your Key)</div>
                        <ul style={{ fontSize: '12px', color: '#666', paddingLeft: '18px', margin: 0, lineHeight: '2' }}>
                          <li>CINC</li>
                          <li>BoomTown</li>
                          <li>Sierra Interactive</li>
                          <li>Lofty (Chime)</li>
                        </ul>
                      </div>
                    </div>

                    <div style={{ fontWeight: 600, color: '#0F1E35', fontSize: '12px', marginBottom: '6px' }}>How It Works</div>
                    <ol style={{ fontSize: '12px', color: '#666', paddingLeft: '18px', margin: '0 0 14px', lineHeight: '2' }}>
                      <li>Go to <strong>Settings → CRM Integrations</strong></li>
                      <li>Select your CRM and enter your API key</li>
                      <li>Click <strong>Save & Validate</strong> — we'll verify the connection</li>
                      <li>Click <strong>Import All Leads</strong> — your contacts import automatically</li>
                      <li>Leads are classified by AI, property ownership is detected, and they appear in your daily brief</li>
                    </ol>

                    <div style={{ fontWeight: 600, color: '#0F1E35', fontSize: '12px', marginBottom: '6px' }}>What Gets Imported</div>
                    <ul style={{ fontSize: '12px', color: '#666', paddingLeft: '18px', margin: '0 0 14px', lineHeight: '2' }}>
                      <li>All contacts — names, emails, phones (including secondary)</li>
                      <li>Addresses — home, mailing, property addresses</li>
                      <li>Lead stage — mapped to our AI classification (buyer, seller, past client, etc.)</li>
                      <li>Deals — active transactions, pipeline stage, price, close date</li>
                      <li>Tags, source, and custom fields</li>
                    </ul>

                    <div style={{ background: '#f5f5f5', borderRadius: '6px', padding: '10px 14px', fontSize: '11px', color: '#555', lineHeight: '1.6' }}>
                      <strong>Your data stays yours.</strong> We import a copy of your leads — your existing CRM is not modified.
                      You can continue using both systems. Import can be re-run anytime to sync new contacts.
                    </div>
                  </div>

                  <div style={{ marginTop: '12px' }}>
                    <button
                      onClick={() => setActiveView('settings')}
                      style={{ padding: '8px 16px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', background: '#0F1E35', color: 'white', border: 'none', cursor: 'pointer' }}
                    >
                      Go to Settings → CRM Integrations
                    </button>
                  </div>

                  <div style={{ fontSize: '11px', color: '#888', marginTop: '10px', lineHeight: '1.6' }}>
                    Don't see your CRM? Select "Other CRM" in Settings or text Jasper at (520) 251-9595 — we'll build the integration for you.
                  </div>
                </Card.Body>
              </Card>

              {/* FAQ */}
              <Card className="mb-3">
                <Card.Body>
                  <h6 style={{ fontWeight: 700, color: '#0F1E35', marginBottom: '12px' }}>Frequently Asked Questions</h6>
                  <div style={{ fontSize: '13px', color: '#444' }}>
                    {[
                      ['Does AI send emails without my permission?', 'Never. Every email goes through your approval queue. You review, edit, and click Send.'],
                      ['Do my clients know AI is involved?', 'No. Emails send from YOUR email address with YOUR name. Clients just see a professional email from their agent.'],
                      ['What if AI classifies someone wrong?', 'You can correct it anytime. Vendors and colleagues can be excluded with one click.'],
                      ['Does this work outside Arizona?', 'Core features (AI classification, daily brief, email drafts, automations) work nationwide. Property intelligence currently covers Maricopa County.'],
                      ['What if I cancel?', 'Cancellation requires 30 days written notice to support@kairosiq.ai. You keep full access during the notice period. After cancellation, your account is deactivated and agent access is removed. Your data is retained for 12 months — reactivate anytime to restore access, or contact support to export your data.'],
                    ].map(([q, a], i) => (
                      <div key={i} style={{ marginBottom: '14px', paddingBottom: '14px', borderBottom: i < 4 ? '1px solid #eee' : 'none' }}>
                        <div style={{ fontWeight: 600, color: '#0F1E35', marginBottom: '4px' }}>{q}</div>
                        <div style={{ color: '#666' }}>{a}</div>
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>

              {/* AI Disclaimers & Accuracy */}
              <Card className="mb-3" style={{ borderLeft: '4px solid #e67e22' }}>
                <Card.Body>
                  <h6 style={{ fontWeight: 700, color: '#0F1E35', marginBottom: '12px' }}>AI Accuracy & Disclaimers</h6>
                  <div style={{ fontSize: '13px', color: '#444', lineHeight: '1.8' }}>
                    <div style={{ background: '#fff8e1', borderRadius: '6px', padding: '12px 14px', marginBottom: '14px', border: '1px solid #ffe082' }}>
                      <div style={{ fontWeight: 700, color: '#e65100', marginBottom: '6px' }}>Important: AI Can Make Mistakes</div>
                      <div style={{ fontSize: '12px', color: '#555', lineHeight: '1.7' }}>
                        AI-generated recommendations, price predictions, and email drafts are based on available data and machine learning models.
                        While we strive for accuracy, AI can produce errors, outdated information, or incomplete analysis.
                        <strong> You are the licensed professional.</strong> Always verify AI outputs before acting on them or presenting to clients.
                      </div>
                    </div>

                    <div style={{ marginBottom: '14px' }}>
                      <div style={{ fontWeight: 600, color: '#0F1E35', marginBottom: '4px' }}>Price Predictions</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Our machine learning valuation model analyzes comparable sales, property characteristics, and market trends to estimate property values.
                        These predictions are NOT appraisals, BPOs, or substitutes for professional valuation.
                        Actual market value may differ based on property condition, improvements, market shifts, and other factors not captured in public data.
                      </div>
                    </div>

                    <div style={{ marginBottom: '14px' }}>
                      <div style={{ fontWeight: 600, color: '#0F1E35', marginBottom: '4px' }}>AI-Generated Email Drafts</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        AI may occasionally generate inaccurate statements, reference incorrect data, or produce content that doesn't reflect your intended message.
                        All drafts are placed in your approval queue for review before sending. <strong>Always read the full draft before clicking Send.</strong>
                        You are responsible for all communications sent from your email account.
                      </div>
                    </div>

                    <div style={{ marginBottom: '14px' }}>
                      <div style={{ fontWeight: 600, color: '#0F1E35', marginBottom: '4px' }}>Lead Classification</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        AI classifies your contacts based on email patterns and communication history. Classifications are probabilistic, not definitive.
                        Colleagues, vendors, or other non-client contacts may occasionally be misidentified. Review and correct classifications as needed.
                      </div>
                    </div>

                    <div>
                      <div style={{ fontWeight: 600, color: '#0F1E35', marginBottom: '4px' }}>Property & County Data</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Property ownership data is sourced from county assessor records, which may be delayed, incomplete, or contain errors.
                        Deed type classifications are based on recorded documents and may not reflect the current legal status of a property.
                        Always verify critical information (foreclosure status, ownership, liens) through official channels before advising clients.
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {/* Privacy & Data Policy */}
              <Card className="mb-3" style={{ borderLeft: '4px solid #8e24aa' }}>
                <Card.Body>
                  <h6 style={{ fontWeight: 700, color: '#0F1E35', marginBottom: '12px' }}>Privacy & Data Policy</h6>
                  <div style={{ fontSize: '13px', color: '#444', lineHeight: '1.8' }}>
                    <div style={{ marginBottom: '14px' }}>
                      <div style={{ fontWeight: 600, color: '#0F1E35', marginBottom: '4px' }}>Your Data Is Yours</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Your client data, email content, lead information, and business communications belong to you.
                        We do not sell, share, or transfer your data to third parties.
                        If you cancel your subscription, your data remains accessible for export.
                      </div>
                    </div>

                    <div style={{ marginBottom: '14px' }}>
                      <div style={{ fontWeight: 600, color: '#0F1E35', marginBottom: '4px' }}>We Do NOT Use Your Data for Model Training</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Your client conversations, email content, and business data are <strong>never used to train AI models</strong>.
                        Our machine learning models are trained on public MLS data and county records — not your private business information.
                        Your data is processed in real-time for your benefit only, then the AI context is discarded.
                      </div>
                    </div>

                    <div style={{ marginBottom: '14px' }}>
                      <div style={{ fontWeight: 600, color: '#0F1E35', marginBottom: '4px' }}>Quality & Compliance Monitoring</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        We log AI recommendations and token usage for quality assurance, accuracy monitoring, and compliance purposes.
                        This allows us to identify and fix issues with AI outputs, ensure regulatory compliance, and improve the platform's reliability.
                        Logs contain metadata (recommendation type, lead classification, timestamps) — not your raw email content or client conversations.
                      </div>
                    </div>

                    <div style={{ marginBottom: '14px' }}>
                      <div style={{ fontWeight: 600, color: '#0F1E35', marginBottom: '4px' }}>Multi-Tenant Isolation</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Every query is scoped to your user ID. You cannot see other agents' data, and they cannot see yours.
                        Even within the same brokerage, each agent's leads, emails, and AI recommendations are completely isolated.
                      </div>
                    </div>

                    <div>
                      <div style={{ fontWeight: 600, color: '#0F1E35', marginBottom: '4px' }}>Security Infrastructure</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        All data encrypted at rest and in transit (AES-256, TLS 1.3). Hosted on AWS with SOC 2 compliant infrastructure.
                        Email credentials stored via OAuth tokens — we never see or store your email password.
                        API keys and secrets managed through AWS Secrets Manager, not hardcoded.
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {/* AI Disclosure to Clients */}
              <Card className="mb-3" style={{ borderLeft: '4px solid #ff8f00' }}>
                <Card.Body>
                  <h6 style={{ fontWeight: 700, color: '#0F1E35', marginBottom: '12px' }}>Recommended: Disclose AI Usage to Your Clients</h6>
                  <div style={{ fontSize: '13px', color: '#444', lineHeight: '1.8' }}>
                    <div style={{ background: '#fff8e1', borderRadius: '6px', padding: '12px 14px', marginBottom: '14px', border: '1px solid #ffe082' }}>
                      <div style={{ fontSize: '12px', color: '#555', lineHeight: '1.7' }}>
                        The National Association of REALTORS (NAR) recommends that real estate professionals disclose the use of AI tools to their clients.
                        As AI becomes integrated into property valuations, market analysis, and client communications, transparency builds trust and protects your business.
                      </div>
                    </div>

                    <div style={{ marginBottom: '14px' }}>
                      <div style={{ fontWeight: 600, color: '#0F1E35', marginBottom: '4px' }}>Why Disclose?</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        <ul style={{ paddingLeft: '20px', margin: '4px 0' }}>
                          <li><strong>Client trust</strong> — Transparency about your tools strengthens the relationship</li>
                          <li><strong>Legal protection</strong> — Disclosure protects you if AI-generated analysis is later questioned</li>
                          <li><strong>Industry best practice</strong> — NAR legal counsel recommends disclosure when AI is used in pricing, valuations, or client communications</li>
                          <li><strong>Professionalism</strong> — Positions you as a tech-forward agent who uses modern tools responsibly</li>
                        </ul>
                      </div>
                    </div>

                    <div style={{ marginBottom: '14px' }}>
                      <div style={{ fontWeight: 600, color: '#0F1E35', marginBottom: '4px' }}>Suggested Disclosure Language</div>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                        You can include this in your listing presentations, buyer consultations, or email signatures:
                      </div>
                      <div style={{ background: '#f5f5f5', borderRadius: '6px', padding: '12px 14px', border: '1px solid #e0e0e0', fontSize: '12px', color: '#333', lineHeight: '1.7', fontStyle: 'italic' }}>
                        "I use AI-powered tools to enhance my market research, property valuations, and client communications.
                        These tools analyze comparable sales data and market trends to support my professional judgment.
                        All AI-generated analyses are reviewed by me before being shared.
                        AI tools assist my work but do not replace my professional expertise, fiduciary duty, or licensed judgment.
                        If you have questions about how I use technology in your transaction, I'm happy to discuss."
                      </div>
                      <div style={{ fontSize: '11px', color: '#888', marginTop: '6px' }}>
                        This is a suggested disclosure only. Consult your broker and/or legal counsel for specific requirements in your state and market.
                      </div>
                    </div>

                    <div>
                      <div style={{ fontWeight: 600, color: '#0F1E35', marginBottom: '4px' }}>Where to Disclose</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        <ul style={{ paddingLeft: '20px', margin: '4px 0' }}>
                          <li><strong>Buyer/seller consultation</strong> — mention during your initial meeting</li>
                          <li><strong>CMA presentations</strong> — note that AI-powered analysis supports your valuation</li>
                          <li><strong>Email signature</strong> — add a brief AI disclosure line</li>
                          <li><strong>Listing agreement addendum</strong> — include in your standard documents if required by your brokerage</li>
                          <li><strong>Offer strategy emails</strong> — already included automatically in KairosIQ-generated drafts</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {/* Account & Data Retention */}
              <Card className="mb-3" style={{ borderLeft: '4px solid #546e7a' }}>
                <Card.Body>
                  <h6 style={{ fontWeight: 700, color: '#0F1E35', marginBottom: '12px' }}>Account & Data Retention</h6>
                  <div style={{ fontSize: '13px', color: '#444', lineHeight: '1.8' }}>

                    <div style={{ marginBottom: '14px' }}>
                      <div style={{ fontWeight: 600, color: '#0F1E35', marginBottom: '4px' }}>Canceling Your Subscription</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Cancellation requires <strong>30 days written notice</strong> to <a href="mailto:support@kairosiq.ai" style={{ color: '#C9952A' }}>support@kairosiq.ai</a>.
                        When you cancel:
                      </div>
                      <ul style={{ fontSize: '12px', color: '#666', paddingLeft: '20px', margin: '6px 0 0' }}>
                        <li>30-day notice period required — submit cancellation to support@kairosiq.ai</li>
                        <li>You retain full access during the 30-day notice period</li>
                        <li>After the notice period, your account and all agent access is deactivated</li>
                        <li>Export your data before cancellation takes effect, or contact support to request an export</li>
                      </ul>
                    </div>

                    <div style={{ marginBottom: '14px' }}>
                      <div style={{ fontWeight: 600, color: '#0F1E35', marginBottom: '4px' }}>Data Retention After Cancellation</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        After cancellation, your data is retained on the platform for <strong>12 months</strong>. This includes:
                      </div>
                      <ul style={{ fontSize: '12px', color: '#666', paddingLeft: '20px', margin: '6px 0 0' }}>
                        <li>Lead and client records</li>
                        <li>CMA reports and property analyses</li>
                        <li>AI conversation history and recommendations</li>
                        <li>Email drafts and sent history</li>
                        <li>Property ownership snapshots and alerts</li>
                        <li>Activity timelines and notes</li>
                      </ul>
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '6px' }}>
                        If you reactivate within 12 months, all your data is restored. After 12 months of inactivity, data is permanently deleted.
                      </div>
                    </div>

                    <div style={{ marginBottom: '14px' }}>
                      <div style={{ fontWeight: 600, color: '#0F1E35', marginBottom: '4px' }}>Why We Retain AI Conversations</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        AI-generated recommendations, email drafts, and coaching conversations are retained as part of your account record. This serves multiple purposes:
                      </div>
                      <ul style={{ fontSize: '12px', color: '#666', paddingLeft: '20px', margin: '6px 0 0' }}>
                        <li><strong>Your protection</strong> — documented record of AI recommendations and your decisions</li>
                        <li><strong>Compliance</strong> — real estate communications are subject to record-keeping requirements</li>
                        <li><strong>Continuity</strong> — if you return, your full history is intact</li>
                        <li><strong>Accuracy</strong> — allows us to track AI quality and correct issues</li>
                      </ul>
                    </div>

                    <div>
                      <div style={{ fontWeight: 600, color: '#0F1E35', marginBottom: '4px' }}>Data Export & Deletion</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        You can request a full export of your data at any time by contacting support@kairosiq.ai.
                        Export includes all leads, activities, reports, AI history, and property records in a standard format.
                        If you want your data permanently deleted before the 12-month retention period, submit a written request to support@kairosiq.ai and we will process it within 30 business days.
                      </div>
                    </div>

                  </div>
                </Card.Body>
              </Card>

              {/* Broker & Sponsor Compliance */}
              <Card className="mb-3" style={{ borderLeft: '4px solid #0F1E35' }}>
                <Card.Body>
                  <h6 style={{ fontWeight: 700, color: '#0F1E35', marginBottom: '12px' }}>Broker & Sponsor Compliance</h6>
                  <div style={{ fontSize: '13px', color: '#444', lineHeight: '1.8' }}>
                    <p style={{ marginBottom: '12px' }}>
                      If your platform access is provided through a sponsor (lender, title company, brokerage, or other partner), the following applies:
                    </p>

                    <div style={{ marginBottom: '14px' }}>
                      <div style={{ fontWeight: 600, color: '#0F1E35', marginBottom: '4px' }}>RESPA Compliance (Lender Sponsors)</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        All sponsor-funded arrangements comply with the Real Estate Settlement Procedures Act (RESPA), Section 8.
                        Your sponsor pays for software access only — there are no fee splits, kickbacks, marketing service agreements, or referral-tied pricing.
                        <strong> You are under no obligation to refer business to your sponsor.</strong> You may refer to any lender, title company, or settlement service provider you choose, on any transaction.
                      </div>
                    </div>

                    <div style={{ marginBottom: '14px' }}>
                      <div style={{ fontWeight: 600, color: '#0F1E35', marginBottom: '4px' }}>Sponsor Data Access</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Sponsors do not have access to your client data, email content, or lead information unless you explicitly opt in.
                        Your CMA reports, offer analyses, and AI recommendations are visible only to you.
                        Sponsors may view aggregated, anonymized performance metrics (if you opt in) but never individual client details.
                      </div>
                    </div>

                    <div style={{ marginBottom: '14px' }}>
                      <div style={{ fontWeight: 600, color: '#0F1E35', marginBottom: '4px' }}>Four Pillars of Compliance</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Every sponsor arrangement at KairosIQ must satisfy four requirements:
                      </div>
                      <ol style={{ fontSize: '12px', color: '#666', paddingLeft: '20px', margin: '6px 0 0' }}>
                        <li><strong>Software-only consideration</strong> — Sponsors pay for platform access, not marketing or lead generation services</li>
                        <li><strong>No pass-through to agents</strong> — Agents receive no payments, rebates, or splits from the sponsor through our platform</li>
                        <li><strong>No referral obligations</strong> — Agents are free to refer to any provider they choose, always</li>
                        <li><strong>Written agreement</strong> — All arrangements are documented with explicit compliance provisions</li>
                      </ol>
                    </div>

                    <div>
                      <div style={{ fontWeight: 600, color: '#0F1E35', marginBottom: '4px' }}>Broker Oversight</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        All MLS data access requires broker approval per your Content License Agreement.
                        Your Designated Broker is informed of any sponsor arrangement involving agents in their brokerage.
                        Platform usage does not modify or override your brokerage's existing policies and procedures.
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {/* Contact Support */}
              <Card style={{ borderLeft: '4px solid #3498db' }}>
                <Card.Body>
                  <h6 style={{ fontWeight: 700, color: '#0F1E35', marginBottom: '12px' }}>Contact Support</h6>
                  <div style={{ fontSize: '13px', color: '#444', lineHeight: '2' }}>
                    <div><strong>Text Jasper:</strong> (520) 251-9595</div>
                    <div><strong>Email:</strong> <a href="mailto:support@kairosiq.ai">support@kairosiq.ai</a></div>
                    <div><strong>Training:</strong> Monthly webinars (recorded, available on demand)</div>
                  </div>
                </Card.Body>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Invite Modal */}
      <Modal show={showInviteModal} onHide={() => !inviteSent && setShowInviteModal(false)} centered>
        <Modal.Header closeButton={!inviteSent}>
          <Modal.Title>
            {inviteSent ? (
              <>
                <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" />
                Invitation Sent!
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faUserPlus} className="me-2" />
                Invite Client
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {inviteSent ? (
            <div className="text-center py-4">
              <div className="success-animation mb-3">
                <FontAwesomeIcon icon={faPaperPlane} size="3x" className="text-success" />
              </div>
              <h5>Invitation sent successfully!</h5>
              <p className="text-muted mb-0">
                {inviteName} will receive an email invitation to access your AI search portal.
              </p>
            </div>
          ) : (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Client Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter client's full name"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  autoFocus
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Email Address</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="client@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
              </Form.Group>
              <Alert variant="info" className="d-flex align-items-center">
                <FontAwesomeIcon icon={faRobot} className="me-2" />
                <div>
                  <small>
                    Your client will get instant access to AI-powered home search on your branded portal
                  </small>
                </div>
              </Alert>
            </Form>
          )}
        </Modal.Body>
        {!inviteSent && (
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowInviteModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleInviteClient}
              disabled={!inviteEmail || !inviteName}
            >
              <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
              Send Invitation
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      {/* Lead Discovery Modal */}
      <Modal show={showDiscovery} onHide={() => setShowDiscovery(false)} size="lg" centered scrollable>
        <Modal.Header style={{ background: '#0F1E35', borderBottom: '2px solid #C9952A' }} closeButton closeVariant="white">
          <Modal.Title style={{ color: 'white', fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '2px' }}>
            <FontAwesomeIcon icon={faSearch} className="me-2" style={{ color: '#C9952A' }} />
            Discover Your Clients
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {discoveryLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" style={{ color: '#C9952A' }} />
              <p className="mt-3" style={{ color: '#666' }}>Scanning your sent emails for contacts...</p>
              <div style={{ width: '60%', margin: '12px auto', background: '#e9ecef', borderRadius: '8px', height: '8px', overflow: 'hidden' }}>
                <div style={{ width: `${discoveryProgress}%`, background: 'linear-gradient(135deg, #C9952A 0%, #E8B44A 100%)', height: '100%', borderRadius: '8px', transition: 'width 0.5s ease' }} />
              </div>
              <small className="text-muted">{discoveryProgress}% complete — this runs in the background</small>
            </div>
          ) : discoveredContacts.length === 0 ? (
            <div className="text-center py-5">
              <FontAwesomeIcon icon={faUsers} size="3x" style={{ color: '#ccc', marginBottom: '16px' }} />
              <h5 style={{ color: '#555' }}>No new contacts found</h5>
              <p className="text-muted">All your email contacts are already in your CRM, or no real estate conversations were found in the last 6 months.</p>
            </div>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h6 style={{ fontWeight: 700, margin: 0 }}>We found {discoveredContacts.length} potential clients in your email</h6>
                  <small className="text-muted">Select the ones you'd like to import to your CRM</small>
                </div>
                <div className="d-flex gap-2">
                  <Button size="sm" variant="outline-primary" onClick={() => {
                    if (selectedDiscovered.size === discoveredContacts.length) {
                      setSelectedDiscovered(new Set());
                    } else {
                      setSelectedDiscovered(new Set(discoveredContacts.map((c: any) => c.email)));
                    }
                  }}>
                    {selectedDiscovered.size === discoveredContacts.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
              </div>

              {discoveredContacts.map((contact: any) => (
                <div key={contact.email}
                  onClick={() => {
                    const newSet = new Set(selectedDiscovered);
                    if (newSet.has(contact.email)) newSet.delete(contact.email);
                    else newSet.add(contact.email);
                    setSelectedDiscovered(newSet);
                  }}
                  style={{
                    padding: '12px 16px', marginBottom: '6px', borderRadius: '8px', cursor: 'pointer',
                    background: selectedDiscovered.has(contact.email) ? '#fff8e1' : '#f8f9fa',
                    border: `1px solid ${selectedDiscovered.has(contact.email) ? '#ffe082' : '#e9ecef'}`,
                    transition: 'all 0.15s'
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-3">
                      <input type="checkbox" checked={selectedDiscovered.has(contact.email)} readOnly
                        style={{ accentColor: '#C9952A', width: '18px', height: '18px' }} />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '14px', color: '#333' }}>
                          {contact.name || contact.email.split('@')[0]}
                        </div>
                        <div style={{ fontSize: '12px', color: '#888' }}>{contact.email}</div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#C9952A' }}>{contact.emailCount} emails</div>
                      <div style={{ fontSize: '11px', color: '#999' }}>
                        {contact.lastContact ? new Date(contact.lastContact).toLocaleDateString() : ''}
                      </div>
                      {contact.lastSubject && (
                        <div style={{ fontSize: '11px', color: '#aaa', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {contact.lastSubject}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </Modal.Body>
        {discoveredContacts.length > 0 && !discoveryLoading && (
          <Modal.Footer style={{ background: '#f8f9fa' }}>
            <div className="d-flex justify-content-between align-items-center w-100">
              <small className="text-muted">{selectedDiscovered.size} of {discoveredContacts.length} selected</small>
              <div className="d-flex gap-2">
                <Button variant="outline-secondary" onClick={() => setShowDiscovery(false)}>Skip for Now</Button>
                <Button
                  disabled={selectedDiscovered.size === 0 || importingLeads}
                  style={{ background: 'linear-gradient(135deg, #C9952A 0%, #E8B44A 100%)', border: 'none', fontWeight: 700 }}
                  onClick={async () => {
                    setImportingLeads(true);
                    try {
                      const toImport = discoveredContacts.filter((c: any) => selectedDiscovered.has(c.email));
                      await axiosInstance.post('/agents/import-leads', { contacts: toImport });
                      // Poll for completion
                      const pollImport = setInterval(async () => {
                        try {
                          const statusRes = await axiosInstance.get('/agents/import-leads-status');
                          const data = statusRes.data;
                          if (data.status === 'complete') {
                            clearInterval(pollImport);
                            toast.success(`Imported ${data.imported || 0} clients to your CRM!`);
                            setShowDiscovery(false);
                            setImportingLeads(false);
                            fetchDashboardData();
                          } else if (data.status === 'error') {
                            clearInterval(pollImport);
                            toast.error(data.message || 'Import failed');
                            setImportingLeads(false);
                          }
                        } catch { clearInterval(pollImport); setImportingLeads(false); }
                      }, 2000);
                    } catch (err) {
                      toast.error('Failed to start import');
                      setImportingLeads(false);
                    }
                  }}
                >
                  {importingLeads ? <><Spinner size="sm" className="me-2" />Importing...</> : `Import ${selectedDiscovered.size} Clients`}
                </Button>
              </div>
            </div>
          </Modal.Footer>
        )}
      </Modal>

      {/* Support Chat Modal */}
      <Modal show={showSupportChat} onHide={() => setShowSupportChat(false)} centered size="lg">
        <Modal.Header closeButton style={{ background: '#0F1E35', color: '#fff', borderBottom: '1px solid #1E3557' }}>
          <Modal.Title style={{ fontSize: '14px', fontWeight: 700 }}>
            <FontAwesomeIcon icon={faEnvelope} className="me-2" style={{ color: '#E8B44A' }} />
            KairosIQ Support
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: '#f8f9fa', padding: 0, height: '400px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
            {supportMessages.length === 0 && (
              <div style={{ textAlign: 'center', color: '#999', padding: '40px 0', fontSize: '13px' }}>
                No messages yet. Send us a message below — we typically respond within a few hours.
              </div>
            )}
            {supportMessages.map((msg: any) => (
              <div key={msg.id} style={{ marginBottom: '12px', display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{
                  maxWidth: '70%', padding: '10px 14px',
                  borderRadius: msg.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                  background: msg.role === 'user' ? '#0F1E35' : '#fff',
                  color: msg.role === 'user' ? '#fff' : '#333',
                  border: msg.role === 'user' ? 'none' : '1px solid #e0e0e0',
                  fontSize: '13px', lineHeight: 1.5
                }}>
                  <div style={{ fontSize: '10px', fontWeight: 600, marginBottom: '4px', color: msg.role === 'user' ? '#E8B44A' : '#999' }}>
                    {msg.role === 'user' ? 'You' : 'KairosIQ Support'}
                  </div>
                  {msg.content}
                  <div style={{ fontSize: '10px', marginTop: '4px', opacity: 0.6 }}>
                    {new Date(msg.timestamp).toLocaleString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ borderTop: '1px solid #e9ecef', padding: '12px 16px', background: '#fff', display: 'flex', gap: '8px' }}>
            <Form.Control
              type="text"
              placeholder="Type your message..."
              value={supportMsg}
              onChange={(e) => setSupportMsg(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === 'Enter' && !e.shiftKey && supportMsg.trim()) {
                  e.preventDefault();
                  setSendingSupport(true);
                  try {
                    const res = await axiosInstance.post('/chat/support/send', {
                      conversationId: supportConvId,
                      message: supportMsg.trim()
                    });
                    if (res.data.success) {
                      setSupportMessages(prev => [...prev, res.data.message]);
                      setSupportMsg('');
                    }
                  } catch { toast.error('Failed to send'); }
                  setSendingSupport(false);
                }
              }}
              style={{ fontSize: '13px' }}
              disabled={sendingSupport}
            />
            <Button size="sm"
              style={{ background: '#0F1E35', border: 'none', whiteSpace: 'nowrap' }}
              disabled={sendingSupport || !supportMsg.trim()}
              onClick={async () => {
                setSendingSupport(true);
                try {
                  const res = await axiosInstance.post('/chat/support/send', {
                    conversationId: supportConvId,
                    message: supportMsg.trim()
                  });
                  if (res.data.success) {
                    setSupportMessages(prev => [...prev, res.data.message]);
                    setSupportMsg('');
                  }
                } catch { toast.error('Failed to send'); }
                setSendingSupport(false);
              }}>
              <FontAwesomeIcon icon={faPaperPlane} />
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AgentDashboardModern;